import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AppConstants, Froala_Constants, availableLngs, urlPathConstants } from 'src/app/utilities/constant';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { UserAuthService } from 'src/app/services/user-auth/user-auth.service';
import { LocalSettingsService } from 'src/app/services/local-settings/local-settings.service';
import { ConfirmationService } from 'src/app/services/confirmation.service';
import { UnsavedChangesService } from 'src/app/services/unsaved-changes.service';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ToastService } from 'src/app/services/toast.service';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import html2pdf from 'html2pdf.js';
import { registerFroalaImageAlignCommand } from 'src/app/utilities/froala-image-align-command';
import { bindFroalaListIndentFix, handleFroalaListBackspace, handleFroalaListIndentCommand, registerFroalaListIndentCommand } from 'src/app/utilities/froala-list-indent-command';
import { applyFroalaTableBorderStyleToSelection, applyFroalaTableCellStyleToSelection, applyFroalaTableVerticalAlignToSelection, bindFroalaTableSelectionHandlers, handleFroalaTableMouseDown, normalizeFroalaTableVerticalAlignCells, rememberCurrentFroalaTableCells, registerFroalaTableBorderCommand } from 'src/app/utilities/froala-table-border-command';

declare const FroalaEditor: any;

export interface Page {
  id: number;
  title: string;
  content: string;
  pagesId: string;
  isDirty: boolean; // Track if page has unsaved changes
  savedContent: string; // Last content persisted to backend (used to revert on Discard)
}

interface SaveRequest {
  pageId: number;
  content: string;
  pagesId: string;
  timestamp: number;
}

@Component({
  selector: 'app-froala-editor',
  templateUrl: './froala-editor.component.html',
  styleUrls: ['./froala-editor.component.scss']
})
export class FroalaEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.froala-editor-fullscreen-active') public isFullscreenMode: boolean = false;
  private readonly froalaLanguageMap: Record<string, string> = {
    da: 'da',
    de: 'de',
    en: 'en_us',
    fr: 'fr',
    it: 'it',
    ko: 'ko',
    nl: 'nl',
    pl: 'pl',
    pt: 'pt_pt',
    sp: 'es',
    sv: 'sv',
    th: 'th',
    tr: 'tr',
    vi: 'vi',
    zh: 'zh_cn',
  };

  @Input() templateTypes: any;
  @Input() templateData: any;
  @Input() title: any;
  @Input() editorClass: string;
  @Input() showInfoIcon: boolean = false;
  @Output() exitEvent = new EventEmitter<void>();
  @ViewChild('TemplateForm') form: NgForm;
  @ViewChild('languageSelect', { read: ElementRef }) languageSelectRef: ElementRef<HTMLElement>;

  // Form and template properties
  public templateForm: FormGroup;
  public templateName: any;
  public selectedLangauge: any;
  public templateId: any;
  public languageChanged: boolean = false;
  public languages = availableLngs.map(language => ({
    label: language.title,
    value: language.val,
  }));

  // Page management
  public pages: Page[] = [];
  public activePage: number = 1;
  private nextPageId: number = 1;

  // Editor management
  private editor: any = null;
  private editorInitialized: boolean = false;
  private initialPageContentLoaded: boolean = false;
  private pendingImageAlignElement: HTMLElement | null = null;
  public options: Object = {};

  // Save management
  private saveQueue: Map<number, SaveRequest> = new Map();
  private isSaving: boolean = false;
  private saveTimer: any = null;
  private readonly SAVE_DELAY = 1000; // 1 second delay for autosave

  // Page switching management
  private isPageSwitching: boolean = false;
  private switchingLock: boolean = false;
  private pageSwitchBlocked: boolean = false; // Block page switching until save completes

  // Content management
  public isUserTyping: boolean = false;
  private preventAutoSave: boolean = false;
  public allowTyping: boolean = true;
  private contentHeight: number = 0;
  // Backend/Puppeteer report contract: A4 = 210 x 297mm.
  // Backend margins are 80 CSS px top/bottom and 10mm left/right.
  // At 96 DPI this gives a usable content box of approximately 718 x 963 CSS px.
  private readonly A4_PAGE_WIDTH_PX: number = 794;
  private readonly A4_PAGE_HEIGHT_PX: number = 1123;
  private readonly A4_TOP_BOTTOM_MARGIN_PX: number = 80;
  private readonly A4_SIDE_MARGIN_PX: number = 37.8;
  private readonly reportPrintableWidth: number = 718;
  private readonly maxPageHeight: number = 963;
  private readonly A4_MEASUREMENT_TOLERANCE_PX: number = 8;
  private lastA4WarningTime: number = 0; // Timestamp for throttling A4 size warning messages
  private hasUnsavedContent: boolean = false; // Track if current page has unsaved content

  // Copy-paste protection
  private lastKnownContent: string = '';
  private lastValidA4Content: string = '';
  private clipboardContent: string = '';
  private preventAutoPaste: boolean = false;
  private loadedContentExceedsA4: boolean = false;
  public a4StatusMessage: string = '';
  public a4StatusMessageType: 'info' | 'warning' = 'info';
  private a4StatusMessageTimer: any = null;
  private promptedLegacyA4OverflowPageKeys: Set<string> = new Set();

  // Baseline of the active page's normalized editor content captured right
  // after load/save. Dirtiness is measured against this so merely focusing the
  // editor (which can re-emit normalized HTML) does not mark the page dirty.
  private activePageBaselineContent: string = '';

  // True only after a genuine user edit (native input / paste / toolbar command)
  // on the current page. Froala programmatically re-normalizes the DOM on the
  // first focus, which fires 'contentChanged' but NOT a real 'input' event; this
  // flag lets us ignore that so focus alone never marks a page dirty.
  private hasUserEditedSinceLoad: boolean = false;
  private userEditListenerAttached: boolean = false;
  // True when the user has changed the template name without saving.
  private isNameDirty: boolean = false;
  
  // Image upload tracking
  private imageUploadInProgress: boolean = false;
  private imageUploadQueue: Set<string> = new Set();
  private pastedImageUploadInProgress: boolean = false;
  private pendingPasteImage: boolean = false; // Flag to track pasted images

  // User info
  private userInfo: any;
  public saveTemplateResponse: any;
  private languageClearKeyHandlers = new WeakSet<HTMLElement>();
  public readonly froalaPopupViewportId = `froala-popup-viewport-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  
  // Ruler feature
  public showRuler: boolean = true;
  public rulerMarks: Array<{position: number, label?: string, isMajor: boolean, type?: 'major' | 'medium' | 'minor'}> = [];
  private rulerUnit: 'inch' | 'cm' | 'px' = 'inch';
  private readonly DPI = 96; // Standard screen DPI
  private readonly A4_WIDTH_INCHES = 8.27;
  @ViewChild('rulerElement') rulerElement: any;
  private resizeObserver: ResizeObserver | null = null;
  @ViewChild('a4ScaleViewport') a4ScaleViewport!: ElementRef<HTMLElement>;
  @ViewChild('a4ZoomStage') a4ZoomStage!: ElementRef<HTMLElement>;
  @ViewChild('a4ScaleCanvas') a4ScaleCanvas!: ElementRef<HTMLElement>;
  // A4 width at 96dpi (210mm); the canvas never deviates from this.
  private readonly A4_CANVAS_WIDTH_PX = 794;
  private readonly A4_CANVAS_HEIGHT_PX = 1123;
  public readonly defaultA4Scale = 1;
    public readonly minA4Scale = 1;
    public readonly maxA4Scale = 1.3;
  public readonly a4ScaleStep = 0.1;
  public a4Scale = 1;
  private a4ScaleObserver: ResizeObserver | null = null;
  private a4ScaleFrameId: number | null = null;
  
  // Guide lines feature
  public hoverGuidePosition: number | null = null; // Position of hover guide line
  public persistentGuides: number[] = []; // Array of persistent guide positions
  public isHoveringRuler: boolean = false; // Track if mouse is over ruler
  
  // Computed property for current page content
  get currentPageContent(): string {
    const currentPage = this.pages.find(page => page.id === this.activePage);
    return currentPage?.content || '';
  }

  set currentPageContent(content: string) {
    this.updatePageContent(content);
  }

  constructor(
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private backendService: BackendCallService,
    public sharedValue: ShareValuesService,
    private toastService: ToastService, 
    private userAuthService: UserAuthService, 
    public localSettingsSrv: LocalSettingsService,
    private translate: TranslateService,
    private unsavedChangesService: UnsavedChangesService
  ) {
    this.userInfo = this.userAuthService.getUserList();
    this.patchFroalaImageAlignCommand();
    this.setupEditorOptions(); // Set up options but don't initialize editor yet
    this.sharedValue.showExitOnEditorTemplate = false;
  }

  private patchFroalaImageAlignCommand(): void {
    registerFroalaImageAlignCommand();
    registerFroalaListIndentCommand();
    registerFroalaTableBorderCommand();
  }

  private patchFroalaPopupContainerRouting(): void {
    const editor = this.editor as any;
    const popups = editor?.popups as any;
    const viewportElement = this.a4ScaleViewport?.nativeElement || document.getElementById(this.froalaPopupViewportId);

    if (!editor || !popups || !viewportElement || popups._blueprintPopupViewportPatched) {
      return;
    }

    const originalSetContainer = typeof popups.setContainer === 'function'
      ? popups.setContainer.bind(popups)
      : null;
    const originalShow = typeof popups.show === 'function'
      ? popups.show.bind(popups)
      : null;

    if (!originalSetContainer || !originalShow || typeof editor.$ !== 'function') {
      return;
    }

    const popupViewport = editor.$(viewportElement);

    popups.setContainer = (popupName: string) => originalSetContainer(popupName, popupViewport);
    popups.show = (popupName: string, left?: number, top?: number, height?: number) => {
      originalSetContainer(popupName, popupViewport);
      return originalShow(popupName, left, top, height);
    };
    popups._blueprintPopupViewportPatched = true;
  }

  private getCurrentImageElement(): HTMLImageElement | null {
    const selectedImage = this.editor?.image?.get?.();
    return (selectedImage?.[0] || selectedImage?.get?.(0) || null) as HTMLImageElement | null;
  }

  private getImageAlignmentTarget(imageElement: HTMLImageElement | null): HTMLElement | null {
    return imageElement?.closest?.('.fr-img-caption') as HTMLElement || imageElement;
  }

  private normalizeFroalaImageAlignment(imageElement: HTMLElement | null, alignValue: string): void {
    if (!imageElement || !['left', 'center', 'right'].includes(alignValue)) {
      return;
    }

    const targetElement = imageElement instanceof HTMLImageElement ? this.getImageAlignmentTarget(imageElement) : imageElement;
    if (!targetElement) {
      return;
    }

    [targetElement, imageElement].forEach((element) => {
      element.classList.remove('fr-dii', 'fr-dib', 'fr-fil', 'fr-fir');
      element.style.removeProperty('display');
      element.style.removeProperty('float');
      element.style.removeProperty('margin-left');
      element.style.removeProperty('margin-right');
      element.style.removeProperty('text-align');
    });

    if (alignValue === 'center') {
      targetElement.classList.add('fr-dib');
      return;
    }

    targetElement.classList.add('fr-dii', alignValue === 'right' ? 'fr-fir' : 'fr-fil');
  }

  private refreshFroalaImageOverlay(imageElement: HTMLElement | null): void {
    const imageToSelect = imageElement instanceof HTMLImageElement
      ? imageElement
      : imageElement?.querySelector?.('img');

    if (!imageToSelect) {
      return;
    }

    requestAnimationFrame(() => {
      this.editor?.events?.trigger?.('image.hideResizer');
      imageToSelect.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      }));
      this.relabelImageAlignCenterOption();
    });
  }

  private relabelImageAlignCenterOption(): void {
    const updateCenterOption = (container: ParentNode | null | undefined) => {
      container?.querySelectorAll?.('.fr-command[data-cmd="imageAlign"][data-param1="center"]').forEach((element: Element) => {
        element.setAttribute('title', 'Align Center');
        element.setAttribute('aria-label', 'Align Center');
        element.querySelector('.fr-sr-only')?.replaceChildren('Align Center');
      });

      container?.querySelectorAll?.('.fr-tooltip').forEach((element: Element) => {
        if (element.textContent?.trim() === 'None') {
          element.textContent = 'Align Center';
        }
      });
    };

    updateCenterOption(this.editor?.$box?.get?.(0));
    updateCenterOption(this.editor?.$tb?.get?.(0));
    updateCenterOption(this.editor?.popups?.get?.('image.edit')?.get?.(0));
    updateCenterOption(this.editor?.popups?.get?.('image.edit')?.[0]);
    updateCenterOption(this.editor?.el?.ownerDocument);
  }

  private getFroalaLanguage(): string {
    const appLanguage = (this.translate.currentLang || this.translate.defaultLang || 'en').toLowerCase();
    return this.froalaLanguageMap[appLanguage] || 'en_us';
  }

  /**
   * Export the current editor content as a PDF while preserving the actual editor
   * layout as closely as possible.
   *
   * The browser print dialog is intentionally kept as a fallback only, because
   * it is not a true PDF generation step and can reflow content based on the
   * browser's print stylesheet. The main path uses html2pdf.js against the live
   * editor markup so the preview matches the editor content more closely.
   */
  public async exportEditorToPDF(): Promise<void> {
    if (!this.editor || !this.editor.el) {
      this.toastService.presentToast(this.translate.instant('editor_loading_please_wait'), 3000, AppConstants.WARNING_SNACKBAR, 'top');
      return;
    }

    try {
      // The backend consumes static_content. The preview deliberately uses the
      // same prepared HTML so the editor, preview and backend share one source.
      const staticContent = this.prepareStaticContentForReport(this.editor.html.get(), true);
      const editorComputedStyle = getComputedStyle(this.editor.el as HTMLElement);
      const previewFontFamily = editorComputedStyle.fontFamily || 'Arial, sans-serif';
      const previewFontSize = editorComputedStyle.fontSize || '12px';
      const previewLineHeight = editorComputedStyle.lineHeight === 'normal'
        ? 'normal'
        : (editorComputedStyle.lineHeight || 'normal');
      const inlineCSS = this.extractInlineCSS();
      const suggestedName = this.templateName
        ? `${this.templateName} - Page ${this.activePage}`
        : `Page ${this.activePage}`;

      const previewHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${this.escapeHtml(suggestedName)}</title>
<style>
${inlineCSS}
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
html, body { margin: 0; padding: 0; background: #525659; }
body { font-family: 'Neue Haas Display Light', Arial, sans-serif; }
.pdf-pages { padding: 24px 0; }
.pdf-sheet {
  position: relative;
  width: 794px;
  height: 1123px;
  box-sizing: border-box;
  padding: 80px 37.8px;
  margin: 0 auto 24px;
  background: #ffffff;
  box-shadow: 0 0 12px rgba(0,0,0,0.5);
  overflow: hidden;
}
.pdf-sheet:last-child { page-break-after: auto; }
.pdf-content-viewport {
  position: relative;
  width: 718px;
  height: 963px;
  overflow: hidden;
}
.pdf-sheet .fr-view,
.pdf-measure-source .fr-view {
  position: relative;
  width: 718px;
  min-height: 0;
  max-width: 718px;
  max-height: none;
  height: auto;
  overflow: visible;
  display: flow-root;
  font-family: ${previewFontFamily};
  font-size: ${previewFontSize};
  line-height: ${previewLineHeight};
  word-wrap: break-word;
}
.pdf-measure-source {
  position: absolute;
  left: -10000px;
  top: 0;
  visibility: hidden;
  width: 794px;
  padding: 80px 37.8px;
  box-sizing: border-box;
  background: #ffffff;
  pointer-events: none;
}
.pdf-page-content { top: 0; }
.pdf-sheet .fr-view img { max-width: 100%; }
.pdf-sheet .fr-view table,
.pdf-measure-source .fr-view table { border-collapse: collapse; max-width: 100%; }
@media print {
  @page { size: A4; margin: 0; }
  html, body { background: #ffffff; }
  .pdf-pages { padding: 0; }
  .pdf-measure-source { display: none; }
  .pdf-sheet {
    width: 794px;
    height: 1123px;
    padding: 80px 37.8px;
    margin: 0;
    box-shadow: none;
    page-break-after: always;
  }
}
</style>
</head>
<body>
<div class="pdf-pages" id="pdf-pages"></div>
<div class="pdf-measure-source"><div class="fr-view" id="pdf-source">${staticContent}</div></div>
<script>
(function () {
  var pagesContainer = document.getElementById('pdf-pages');
  var source = document.getElementById('pdf-source');
  var pageHeight = ${this.maxPageHeight};
  var pageBreakSlack = 6;
  var renderTimer = null;

  function renderPages() {
    if (!pagesContainer || !source) {
      return;
    }

    var measuredHeight = getRenderedContentHeight(source);
    var pageOffsets = getPageOffsets(source, measuredHeight);
    pagesContainer.innerHTML = '';

    for (var pageIndex = 0; pageIndex < pageOffsets.length; pageIndex += 1) {
      var sheet = document.createElement('div');
      var viewport = document.createElement('div');
      var pageContent = document.createElement('div');
      var pageOffset = pageOffsets[pageIndex];
      var nextPageOffset = pageOffsets[pageIndex + 1] || Math.min(pageOffset + pageHeight, measuredHeight);
      var visibleHeight = Math.max(0, Math.min(pageHeight, nextPageOffset - pageOffset));

      sheet.className = 'pdf-sheet';
      viewport.className = 'pdf-content-viewport';
      viewport.style.height = visibleHeight + 'px';
      pageContent.className = 'fr-view pdf-page-content';
      pageContent.style.top = '-' + pageOffset + 'px';
      pageContent.innerHTML = source.innerHTML;

      viewport.appendChild(pageContent);
      sheet.appendChild(viewport);
      pagesContainer.appendChild(sheet);
    }
  }

  function getPageOffsets(root, measuredHeight) {
    var offsets = [0];
    var currentOffset = 0;

    while (currentOffset + pageHeight < measuredHeight - pageBreakSlack) {
      var targetOffset = currentOffset + pageHeight;
      var nextOffset = getNextPageOffset(root, currentOffset, targetOffset);

      if (nextOffset <= currentOffset + pageBreakSlack) {
        nextOffset = targetOffset;
      }

      offsets.push(nextOffset);
      currentOffset = nextOffset;
    }

    return offsets;
  }

  function getNextPageOffset(root, currentOffset, targetOffset) {
    var minUsefulOffset = currentOffset + (pageHeight * 0.55);
    var candidates = getPageBreakCandidates(root).filter(function (candidate) {
      return candidate > currentOffset + pageBreakSlack && candidate <= targetOffset;
    });

    if (!candidates.length) {
      return targetOffset;
    }

    var bestCandidate = Math.max.apply(Math, candidates);
    return bestCandidate >= minUsefulOffset ? bestCandidate : targetOffset;
  }

  function getPageBreakCandidates(root) {
    var rootRect = root.getBoundingClientRect();
    var candidates = [];

    Array.prototype.forEach.call(root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, table, img, svg, hr, iframe, video'), function (element) {
      var rect = element.getBoundingClientRect();

      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return;
      }

      var top = Math.round(rect.top - rootRect.top);
      var bottom = Math.round(rect.bottom - rootRect.top);

      if (top > pageBreakSlack) {
        candidates.push(top);
      }

      if (bottom > pageBreakSlack) {
        candidates.push(bottom);
      }
    });

    return candidates;
  }

  function getRenderedContentHeight(root) {
    var rootRect = root.getBoundingClientRect();
    var bottom = 0;

    function includeRect(rect) {
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return;
      }

      bottom = Math.max(bottom, rect.bottom - rootRect.top);
    }

    Array.prototype.forEach.call(root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, table, img, svg, hr, iframe, video'), function (element) {
      if ((element.textContent || '').replace(/\u00a0/g, ' ').trim() || element.querySelector('br, img, svg, hr, iframe, video') || ['TABLE', 'IMG', 'SVG', 'HR', 'IFRAME', 'VIDEO'].indexOf(element.tagName) !== -1) {
        includeRect(element.getBoundingClientRect());
      }
    });

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node = walker.nextNode();
    while (node) {
      if ((node.textContent || '').replace(/\u00a0/g, ' ').trim()) {
        var range = document.createRange();
        range.selectNodeContents(node);
        Array.prototype.forEach.call(range.getClientRects(), includeRect);
        range.detach();
      }

      node = walker.nextNode();
    }

    return Math.ceil(Math.max(0, bottom));
  }

  function scheduleRender() {
    if (renderTimer) {
      window.clearTimeout(renderTimer);
    }
    renderTimer = window.setTimeout(renderPages, 0);
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleRender);
  }

  window.addEventListener('load', scheduleRender);
  Array.prototype.forEach.call(document.images, function (image) {
    if (!image.complete) {
      image.addEventListener('load', scheduleRender);
      image.addEventListener('error', scheduleRender);
    }
  });

  scheduleRender();
  window.setTimeout(scheduleRender, 60);
  window.setTimeout(scheduleRender, 300);
}());
</script>
</body>
</html>`;

      const previewWidth = Math.min(Math.max(900, Math.ceil(this.A4_CANVAS_WIDTH_PX * this.a4Scale + 140)), Math.floor(window.screen.availWidth * 0.9));
      const previewHeight = Math.floor(window.screen.availHeight * 0.9);
      const previewWindow = window.open(
        '',
        '_blank',
        `toolbar=0,location=0,menubar=0,status=0,scrollbars=1,resizable=1,width=${previewWidth},height=${previewHeight}`
      );

      if (!previewWindow) {
        this.toastService.presentToast(
          'Pop-up blocked. Please allow pop-ups to open the PDF preview.',
          4000,
          AppConstants.WARNING_SNACKBAR,
          'top'
        );
        return;
      }

      previewWindow.document.open();
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
    } catch (error) {
      console.error('Error opening PDF preview:', error);
      this.toastService.presentToast(this.translate.instant('failed_to_export_pdf_retry'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
    }
  }

  /**
   * Extract relevant CSS rules from the current document as inline text.
   * This avoids relying on external <link> stylesheets that won't load in a new window.
   */
  private extractInlineCSS(): string {
    const cssTexts: string[] = [];

    try {
      // Iterate through all stylesheets in the document
      for (let i = 0; i < document.styleSheets.length; i++) {
        try {
          const sheet = document.styleSheets[i];
          // Skip cross-origin stylesheets we can't read (they'll throw)
          const rules = sheet.cssRules || sheet.rules;
          if (!rules) continue;

          for (let j = 0; j < rules.length; j++) {
            const rule = rules[j];
            const ruleText = rule.cssText || '';

            // Include Froala view styles, table styles, heading styles, font styles, and general text styles
            if (ruleText.includes('.fr-view') ||
                ruleText.includes('.fr-element') ||
                ruleText.includes('.fr-fil') ||
                ruleText.includes('.fr-fir') ||
                ruleText.includes('.fr-dii') ||
                ruleText.includes('.fr-dib') ||
                ruleText.includes('froala-highlighter') ||
                ruleText.includes('@font-face') ||
                ruleText.includes('--font-')) {
              cssTexts.push(ruleText);
            }
          }
        } catch (e) {
          // Can't access cross-origin stylesheet rules — skip silently
          continue;
        }
      }
    } catch (e) {
      console.warn('Could not extract CSS rules:', e);
    }

    return cssTexts.join('\n');
  }

  private prepareStaticContentForReport(content: string, useLiveEditorStyles: boolean = false): string {
    if (!content) {
      return content;
    }

    const container = document.createElement('div');
    container.innerHTML = content;

    // Selection/highlight classes are editor-only UI. They must never be
    // persisted to static_content or affect report styling.
    this.removeTransientEditorSelectionClasses(container);

    // Do not trim trailing blocks or zero the last block's spacing. Those
    // spaces are part of the user's document and the backend report contract.
    this.inlineTableStyles(container, useLiveEditorStyles, true);
    this.inlineImageStyles(container, useLiveEditorStyles);
    this.inlineSvgStyles(container);
    this.inlineEmojiIcons(container);
    if (useLiveEditorStyles) {
      this.syncPreviewBlockSpacing(container);
      this.inlineLiveEditorStyles(container);
    }
    this.normalizeReportFloatRows(container);

    return container.innerHTML;
  }

  private removeTransientEditorSelectionClasses(container: HTMLElement): void {
    container.querySelectorAll('.blueprint-table-selected-cell, .fr-selected-cell, .fr-cell-fixed, .fr-cell-handler, [class*="blueprint-selected-neighbor-"]').forEach((element) => {
      Array.from(element.classList)
        .filter(className => className.startsWith('blueprint-selected-neighbor-'))
        .forEach(className => element.classList.remove(className));
      element.classList.remove('blueprint-table-selected-cell', 'fr-selected-cell', 'fr-cell-fixed', 'fr-cell-handler');
    });
  }

  /**
   * Rebuild report tables as a single grid: each cell owns its right/bottom
   * edges, while the first row/column also owns the outer top/left edges.
   */
  private normalizeTableBordersForReport(table: HTMLTableElement): void {
    const tableBorders = {
      top: this.getInlineBorderSide(table, 'top'),
      right: this.getInlineBorderSide(table, 'right'),
      bottom: this.getInlineBorderSide(table, 'bottom'),
      left: this.getInlineBorderSide(table, 'left')
    };
    const rows = Array.from(table.rows);
    const hasExplicitCellBorders = rows.some(row =>
      Array.from(row.cells).some(cell => ['top', 'right', 'bottom', 'left'].some(side =>
        this.isVisibleBorder(this.getInlineBorderSide(cell, side))
      ))
    );

    if (!hasExplicitCellBorders && !Object.values(tableBorders).some(border => this.isVisibleBorder(border))) {
      return;
    }

    table.style.removeProperty('border');
    ['top', 'right', 'bottom', 'left'].forEach(side => {
      table.style.setProperty(`border-${side}`, 'none', 'important');
    });
    table.style.setProperty('border-collapse', 'separate', 'important');
    table.style.setProperty('border-spacing', '0', 'important');
    table.style.setProperty('max-width', '100%');

    rows.forEach((row, rowIndex) => {
      const cells = Array.from(row.cells);

      cells.forEach((cell, cellIndex) => {
        const borders = {
          top: this.getInlineBorderSide(cell, 'top') || tableBorders.top,
          right: this.getInlineBorderSide(cell, 'right') || tableBorders.right,
          bottom: this.getInlineBorderSide(cell, 'bottom') || tableBorders.bottom,
          left: this.getInlineBorderSide(cell, 'left') || tableBorders.left
        };

        cell.style.removeProperty('border');
        ['top', 'right', 'bottom', 'left'].forEach(side => {
          cell.style.setProperty(`border-${side}`, 'none', 'important');
        });

        if (rowIndex === 0 && borders.top) {
          cell.style.setProperty('border-top', borders.top, 'important');
        }
        if (cellIndex === 0 && borders.left) {
          cell.style.setProperty('border-left', borders.left, 'important');
        }
        if (borders.right) {
          cell.style.setProperty('border-right', borders.right, 'important');
        }
        if (borders.bottom) {
          cell.style.setProperty('border-bottom', borders.bottom, 'important');
        }
      });
    });
  }

  private getInlineBorderSide(element: HTMLElement, side: string): string {
    const sideBorder = element.style.getPropertyValue(`border-${side}`).trim();

    if (sideBorder) {
      return sideBorder;
    }

    return element.style.getPropertyValue('border').trim();
  }

  private isVisibleBorder(border: string): boolean {
    return !!border && border !== 'none' && !border.startsWith('0px');
  }

  private trimTrailingEmptyBlocks(container: HTMLElement): void {
    while (container.lastChild) {
      const lastChild = container.lastChild;

      if (lastChild.nodeType === Node.TEXT_NODE && !(lastChild.textContent || '').trim()) {
        container.removeChild(lastChild);
        continue;
      }

      if (lastChild instanceof HTMLBRElement) {
        container.removeChild(lastChild);
        continue;
      }

      if (lastChild instanceof HTMLElement &&
          lastChild.matches('p, div, section, article, blockquote, ul, ol, li, h1, h2, h3, h4, h5, h6')) {
        this.trimTrailingEmptyBlocks(lastChild);
      }

      if (this.isEmptyReportNode(lastChild)) {
        container.removeChild(lastChild);
        continue;
      }

      break;
    }
  }

  /**
   * Zero out the bottom margin/padding of the last rendered block so a near-full
   * page does not spill its trailing whitespace onto an extra empty page in the
   * PDF preview and backend report.
   */
  private removeTrailingBlockSpacing(container: HTMLElement): void {
    let lastElement = container.lastElementChild as HTMLElement | null;

    while (lastElement) {
      lastElement.style.setProperty('margin-bottom', '0', 'important');
      lastElement.style.setProperty('padding-bottom', '0', 'important');
      lastElement.style.setProperty('break-after', 'auto', 'important');
      lastElement.style.setProperty('page-break-after', 'auto', 'important');

      if (lastElement.matches('table, img, hr, iframe, video, svg')) {
        break;
      }

      lastElement = lastElement.lastElementChild as HTMLElement | null;
    }
  }

  private isEmptyReportNode(node: ChildNode): boolean {
    if (node.nodeType === Node.TEXT_NODE) {
      return !(node.textContent || '').trim();
    }

    if (!(node instanceof HTMLElement)) {
      return false;
    }

    if (node.matches('img, table, hr, iframe, video, svg')) {
      return false;
    }

    if (node.matches('p, div, section, article, blockquote, ul, ol, li, h1, h2, h3, h4, h5, h6')) {
      return !(node.textContent || '').replace(/\u00a0/g, ' ').trim() && !node.querySelector('img, table, hr, iframe, video, svg');
    }

    return false;
  }

  /**
   * Build PDF preview content from Froala's full-document API. The preview uses
   * the same prepared HTML that is sent as static_content, so it does not depend
   * on the editor viewport height.
   */
  private preparePreviewContentForPDF(): string {
    const container = document.createElement('div');
    container.innerHTML = this.editor.html.get();

    this.inlineTableStyles(container, true, true);
    this.inlineImageStyles(container);
    this.inlineSvgStyles(container);
    this.inlineEmojiIcons(container);
    this.syncPreviewBlockSpacing(container);
    this.removeTransientEditorSelectionClasses(container);

    return container.innerHTML;
  }

  /**
   * Copy each block element's own live margin from the editor so print
   * spacing matches exactly, instead of relying on one generic CSS rule.
   */
  private syncPreviewBlockSpacing(container: HTMLElement): void {
    const blockSelector = 'p, h1, h2, h3, h4, h5, h6, li, blockquote';
    const editorBlocks = Array.from(this.editor?.el?.querySelectorAll?.(blockSelector) || []) as HTMLElement[];
    const containerBlocks = Array.from(container.querySelectorAll(blockSelector)) as HTMLElement[];

    containerBlocks.forEach((block, index) => {
      const editorBlock = editorBlocks[index];
      if (!editorBlock) {
        return;
      }

      const computedStyle = getComputedStyle(editorBlock);
      block.style.setProperty('margin', `${computedStyle.marginTop} ${computedStyle.marginRight} ${computedStyle.marginBottom} ${computedStyle.marginLeft}`);
    });
  }

  private inlineLiveEditorStyles(container: HTMLElement): void {
    if (!this.editor?.el) {
      return;
    }

    const selector = 'p, div, h1, h2, h3, h4, h5, h6, li, blockquote, span, strong, b, em, i, u, a, sub, sup, table, thead, tbody, tr, td, th';
    const editorElements = Array.from(this.editor.el.querySelectorAll(selector)) as HTMLElement[];
    const reportElements = Array.from(container.querySelectorAll(selector)) as HTMLElement[];
    const commonProperties = [
      'display',
      'box-sizing',
      'font-family',
      'font-size',
      'font-weight',
      'font-style',
      'line-height',
      'color',
      'background-color',
      'text-align',
      'text-decoration',
      'text-transform',
      'letter-spacing',
      'vertical-align',
      'white-space',
      'word-break',
      'word-wrap',
      'overflow-wrap'
    ];
    const layoutProperties = [
      'margin-top',
      'margin-right',
      'margin-bottom',
      'margin-left',
      'padding-top',
      'padding-right',
      'padding-bottom',
      'padding-left',
      'border-top',
      'border-right',
      'border-bottom',
      'border-left',
      'border-collapse',
      'border-spacing',
      'table-layout'
    ];

    reportElements.forEach((element, index) => {
      const editorElement = editorElements[index];
      if (!editorElement) {
        return;
      }

      this.copyComputedStyles(element, editorElement, commonProperties);

      if (this.shouldInlineLiveLayoutStyle(element)) {
        this.copyComputedStyles(element, editorElement, layoutProperties);
      }
    });
  }

  private normalizeReportFloatRows(container: HTMLElement): void {
    this.normalizeReportFloatRowsIn(container);
  }

  private normalizeReportFloatRowsIn(parent: Element): void {
    let row: HTMLElement[] = [];

    Array.from(parent.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) {
        return;
      }

      if (this.isReportFloatedBlock(child)) {
        row.push(child);
      } else {
        this.normalizeReportFloatRow(row);
        row = [];
      }

      this.normalizeReportFloatRowsIn(child);
    });

    this.normalizeReportFloatRow(row);
  }

  private isReportFloatedBlock(element: HTMLElement): boolean {
    const floatValue = (element.style.getPropertyValue('float') || element.style.cssFloat || '').trim().toLowerCase();
    return ['left', 'right'].includes(floatValue) && this.getPixelSize(element.style.getPropertyValue('width')) > 0;
  }

  private normalizeReportFloatRow(row: HTMLElement[]): void {
    if (row.length < 2) {
      return;
    }

    const rowParts = row.map((element) => {
      const width = this.getPixelSize(element.style.getPropertyValue('width'));
      const marginLeft = this.getPixelSize(element.style.getPropertyValue('margin-left'));
      const marginRight = this.getPixelSize(element.style.getPropertyValue('margin-right'));

      return {
        element,
        width,
        marginLeft,
        marginRight,
        outerWidth: width + marginLeft + marginRight
      };
    });
    const totalWidth = rowParts.reduce((sum, part) => sum + part.outerWidth, 0);

    if (totalWidth <= this.reportPrintableWidth + 1) {
      return;
    }

    rowParts.forEach((part) => {
      this.setReportPercentLength(part.element, 'width', part.width, totalWidth);
      this.setReportPercentLength(part.element, 'margin-left', part.marginLeft, totalWidth);
      this.setReportPercentLength(part.element, 'margin-right', part.marginRight, totalWidth);
      this.removeReportFixedHeight(part.element);
      this.normalizeReportFloatDescendants(part.element, part.width);
    });
  }

  private normalizeReportFloatDescendants(parent: HTMLElement, parentWidth: number): void {
    const selector = 'p, div, h1, h2, h3, h4, h5, h6, li, blockquote';

    Array.from(parent.querySelectorAll(selector)).forEach((element: HTMLElement) => {
      const width = this.getPixelSize(element.style.getPropertyValue('width'));

      if (width > 0 && width <= parentWidth + 2) {
        element.style.setProperty('width', '100%', 'important');
        element.style.removeProperty('min-width');
        element.style.setProperty('max-width', '100%', 'important');
      }

      this.removeReportFixedHeight(element);
    });
  }

  private setReportPercentLength(element: HTMLElement, property: string, value: number, total: number): void {
    if (value <= 0 || total <= 0) {
      element.style.setProperty(property, '0px', 'important');
      return;
    }

    element.style.setProperty(property, `${Number(((value / total) * 100).toFixed(4))}%`, 'important');
  }

  private removeReportFixedHeight(element: HTMLElement): void {
    element.style.removeProperty('height');
    element.style.removeProperty('min-height');
    element.style.removeProperty('max-height');
  }

  private shouldInlineLiveLayoutStyle(element: HTMLElement): boolean {
    return /^(DIV|P|H[1-6]|LI|BLOCKQUOTE|TABLE|THEAD|TBODY|TR|TD|TH)$/.test(element.tagName);
  }

  private inlineTableStyles(
    container: HTMLElement,
    useLiveEditorStyles: boolean = false,
    includeLiveLayoutStyles: boolean = false
  ): void {
    const tableStyleMap: Record<string, string> = {
      noborder: '1px solid #DDD',
      addborder: '1px solid #000',
      dashedborder: '1px dashed #000',
    };

    const editorTables = useLiveEditorStyles
      ? Array.from(this.editor?.el?.querySelectorAll?.('table') || []) as HTMLTableElement[]
      : [];
    const containerTables = Array.from(container.querySelectorAll('table')) as HTMLTableElement[];

    containerTables.forEach((table: HTMLTableElement, tableIndex: number) => {
      const editorTable = editorTables[tableIndex];

      // Preserve the user's existing HTML attributes/styles. Only add neutral
      // report-safe table defaults when they are genuinely absent.
      if (!table.style.getPropertyValue('border-collapse')) {
        table.style.setProperty('border-collapse', 'collapse');
      }
      if (!table.style.getPropertyValue('border-spacing')) {
        table.style.setProperty('border-spacing', '0');
      }
      table.style.setProperty('box-sizing', 'border-box');
      table.style.setProperty('max-width', 'calc(100% - 1px)');

      if (useLiveEditorStyles && editorTable) {
        if (includeLiveLayoutStyles) {
          this.copyComputedStyles(table, editorTable, [
            'width', 'max-width', 'table-layout', 'border-collapse', 'border-spacing'
          ]);
        }
        this.copyLiveBoxSpacing(table, editorTable);
        this.copyLiveBorderStyles(table, editorTable, 'none');
        const editorCells = Array.from(editorTable.querySelectorAll('td, th')) as HTMLElement[];
        const cells = Array.from(table.querySelectorAll('td, th')) as HTMLElement[];
        cells.forEach((cell, cellIndex) => {
          const editorCell = editorCells[cellIndex];
          const verticalAlign = this.getTableCellVerticalAlign(cell, editorCell);
          cell.setAttribute('valign', verticalAlign);
          cell.style.setProperty('vertical-align', verticalAlign);
          if (includeLiveLayoutStyles && editorCell) {
            this.copyComputedStyles(cell, editorCell, [
              'width', 'min-width', 'max-width', 'padding', 'font-family', 'font-size',
              'font-weight', 'font-style', 'line-height', 'color', 'text-align',
              'vertical-align', 'word-break', 'word-wrap', 'overflow-wrap', 'white-space'
            ]);

            const editorCellContent = Array.from(editorCell.querySelectorAll('*')) as HTMLElement[];
            const cellContent = Array.from(cell.querySelectorAll('*')) as HTMLElement[];
            cellContent.forEach((element, elementIndex) => {
              const editorElement = editorCellContent[elementIndex];
              if (editorElement) {
                this.copyComputedStyles(element, editorElement, [
                  'max-width', 'word-break', 'word-wrap', 'overflow-wrap'
                ]);
              }
            });
          }
          this.copyLiveBoxSpacing(cell, editorCell);
          this.copyLiveBorderStyles(cell, editorCell, 'none');
        });
      }

      // Custom border command classes are authoritative when present. They are
      // intentionally resolved after live computed styles so the saved report
      // matches the border the user selected.
      Object.keys(tableStyleMap).forEach(styleClass => {
        if (!table.classList.contains(styleClass)) return;
        const border = tableStyleMap[styleClass];
        this.setReportBorderStyles(table, border);
      });

      const cells = Array.from(table.querySelectorAll('td, th')) as HTMLElement[];
      cells.forEach((cell) => {
        cell.style.setProperty('box-sizing', 'border-box');
        this.applyTableCellBorderClass(cell);
        this.applyTableCellStyleClass(cell);
        if (!cell.getAttribute('valign') && cell.style.verticalAlign) {
          cell.setAttribute('valign', cell.style.verticalAlign);
        }
      });

      this.normalizeTableBordersForReport(table);
    });
  }

  /**
   * Inline the editor's resolved CSS values for report HTML. The report renderer
   * does not necessarily load the editor stylesheet, so table sizing and text
   * wrapping must travel with the saved markup rather than relying on classes.
   */
  private copyComputedStyles(target: HTMLElement, source: HTMLElement, properties: string[]): void {
    const computedStyle = getComputedStyle(source);

    properties.forEach((property) => {
      const rawValue = computedStyle.getPropertyValue(property);
      const value = property === 'font-family' ? this.getReportFontFamilyValue(rawValue) : rawValue;
      if (value) {
        target.style.setProperty(property, value, 'important');
      }
    });
  }

  private getReportFontFamilyValue(fontFamily: string): string {
    if (!fontFamily) {
      return fontFamily;
    }

    if (/NeueHaas|Neue Haas|var\(--font-/i.test(fontFamily)) {
      return '"NeueHaasDisplayLight", "Neue Haas Grotesk Text Pro", Arial, sans-serif';
    }

    return fontFamily;
  }

  private getTableCellVerticalAlign(cell: HTMLElement, editorCell?: HTMLElement): string {
    return this.normalizeTableCellVerticalAlign(cell.style.getPropertyValue('vertical-align') || cell.style.verticalAlign)
      || this.normalizeTableCellVerticalAlign(cell.getAttribute('valign'))
      || this.normalizeTableCellVerticalAlign(editorCell?.style.getPropertyValue('vertical-align') || editorCell?.style.verticalAlign)
      || this.normalizeTableCellVerticalAlign(editorCell?.getAttribute('valign'))
      || 'top';
  }

  private normalizeTableCellVerticalAlign(value: string | null | undefined): string | null {
    const normalizedValue = (value || '').trim().toLowerCase();

    return ['top', 'middle', 'bottom'].includes(normalizedValue) ? normalizedValue : null;
  }

  private applyTableCellBorderClass(cell: HTMLElement): void {
    if (cell.classList.contains('noborder')) {
      this.setReportDefaultTableBorderStyles(cell);
      return;
    }

    if (cell.classList.contains('dashedborder')) {
      this.applyReportCellBorderFromClass(cell, '1px dashed #000');
      return;
    }

    if (cell.classList.contains('addborder')) {
      this.applyReportCellBorderFromClass(cell, '1px solid #000');
    }
  }

  /**
   * Apply a class-driven cell border without discarding per-side inline borders.
   *
   * In a collapsed table an edge is shared by two cells, so Remove Border writes
   * the neutral gridline directly onto the *unselected* neighbour's shared side
   * (e.g. the cell above gets `border-bottom: 1px solid #DDD`). The report grid
   * built by normalizeTableBordersForReport() gives every edge a single owning
   * cell - interior top/left edges are owned by the neighbour above/left - so
   * blanket-writing all four sides from the cell's border class would resurrect
   * the black/dashed edge that the editor no longer shows.
   */
  private applyReportCellBorderFromClass(cell: HTMLElement, border: string): void {
    ['top', 'right', 'bottom', 'left'].forEach((side) => {
      if (cell.style.getPropertyValue(`border-${side}`).trim()) {
        return;
      }

      cell.style.setProperty(`border-${side}`, border, 'important');
    });

    cell.style.setProperty('mso-border-alt', `${border.includes('dashed') ? 'dashed' : 'solid'} #000000 0.75pt`);
  }

  private setReportDefaultTableBorderStyles(cell: HTMLElement): void {
    const defaultBorder = '1px solid #DDD';

    ['top', 'right', 'bottom', 'left'].forEach((side) => {
      const existingBorder = this.getInlineBorderSide(cell, side);

      // A noborder cell must always resolve to the neutral table gridline in
      // Preview, even if a stale neighbor-owned border is still present in the
      // editor DOM. The editor renders the neutral gridline; the preview must
      // match it exactly instead of preserving any old black edge.
      if (cell.classList.contains('noborder') || !this.isVisibleBorder(existingBorder) || this.isDefaultTableBorder(existingBorder)) {
        cell.style.setProperty(`border-${side}`, defaultBorder, 'important');
      }
    });
  }

  private isDefaultTableBorder(border: string): boolean {
    const normalizedBorder = (border || '').trim().toLowerCase().replace(/\s+/g, ' ');

    return normalizedBorder === '1px solid #ddd' || normalizedBorder === '1px solid rgb(221, 221, 221)';
  }

  private applyTableCellStyleClass(cell: HTMLElement): void {
    if (cell.classList.contains('fr-highlighted')) {
      this.setReportBorderStyles(cell, '1px double red');
      return;
    }

    if (cell.classList.contains('fr-thick')) {
      this.setThickReportCellBorder(cell);
    }
  }

  private setThickReportCellBorder(cell: HTMLElement): void {
    const borderSides = ['top', 'right', 'bottom', 'left'];

    borderSides.forEach((side) => {
      const borderStyle = this.getFirstCssValue(cell.style.getPropertyValue(`border-${side}-style`) || cell.style.borderStyle || 'solid');
      const borderColor = this.normalizeCssColorValue(cell.style.getPropertyValue(`border-${side}-color`) || cell.style.borderColor || '#000');
      const normalizedStyle = !borderStyle || borderStyle === 'none' ? 'solid' : borderStyle;
      const normalizedColor = !borderColor || borderColor === 'initial' ? '#000' : borderColor;

      cell.style.setProperty(`border-${side}`, `2px ${normalizedStyle} ${normalizedColor}`, 'important');
    });

    cell.style.setProperty('mso-border-alt', 'solid #000000 1.5pt');
  }

  private getFirstCssValue(value: string): string {
    return (value || '').trim().split(/\s+/)[0] || '';
  }

  private normalizeCssColorValue(value: string): string {
    const trimmedValue = (value || '').trim();

    if (/^(rgb|hsl)a?\(/i.test(trimmedValue)) {
      return trimmedValue;
    }

    return this.getFirstCssValue(trimmedValue);
  }

  /**
   * Copy a live element's computed margin/padding so print spacing always
   * matches the editor; falls back to fallbackPadding when no live match exists.
   */
  private copyLiveBoxSpacing(target: HTMLElement, source?: HTMLElement, fallbackPadding?: string): void {
    if (!source) {
      if (fallbackPadding) {
        target.style.setProperty('padding', fallbackPadding);
      }
      return;
    }
    const computedStyle = getComputedStyle(source);
    target.style.setProperty('margin', `${computedStyle.marginTop} ${computedStyle.marginRight} ${computedStyle.marginBottom} ${computedStyle.marginLeft}`);
    target.style.setProperty('padding', `${computedStyle.paddingTop} ${computedStyle.paddingRight} ${computedStyle.paddingBottom} ${computedStyle.paddingLeft}`);
  }

  /**
   * Copy a live element's computed border so print borders match the editor's
   * actual weight/color instead of a hardcoded value; falls back when no live
   * border is found (e.g. borderless elements or no matching live element).
   */
  private copyLiveBorderStyles(target: HTMLElement, source: HTMLElement | undefined, fallbackBorder: string): void {
    const computedStyle = source ? getComputedStyle(source) : null;

    if (!computedStyle) {
      this.setReportBorderStyles(target, fallbackBorder);
      return;
    }

    const sides = ['Top', 'Right', 'Bottom', 'Left'];
    let hasAnyBorder = false;

    sides.forEach(side => {
      const width = computedStyle[`border${side}Width`];
      const style = computedStyle[`border${side}Style`];
      const color = computedStyle[`border${side}Color`];

      if (!width || !style || style === 'none' || parseFloat(width) === 0) {
        target.style.setProperty(`border-${side.toLowerCase()}`, 'none', 'important');
        return;
      }

      hasAnyBorder = true;
      target.style.setProperty(`border-${side.toLowerCase()}`, `${width} ${style} ${color}`, 'important');
    });

    if (!hasAnyBorder) {
      this.setReportBorderStyles(target, fallbackBorder);
    }
  }

  private setReportBorderStyles(element: HTMLElement, border: string): void {
    const borderProperties = ['border', 'border-top', 'border-right', 'border-bottom', 'border-left'];

    borderProperties.forEach(property => {
      element.style.setProperty(property, border, 'important');
    });

    if (border === 'none') {
      element.style.setProperty('mso-border-alt', 'none');
      return;
    }

    const borderStyle = border.includes('dashed') ? 'dashed' : 'solid';
    element.style.setProperty('mso-border-alt', `${borderStyle} #000000 0.75pt`);
  }

  private inlineImageStyles(container: HTMLElement, useLiveEditorStyles: boolean = false): void {
    const editorImages = useLiveEditorStyles
      ? Array.from(this.editor?.el?.querySelectorAll?.('img') || []) as HTMLImageElement[]
      : [];

    container.querySelectorAll('img').forEach((image: HTMLImageElement, index: number) => {
      const editorImage = useLiveEditorStyles
        ? (editorImages[index]?.src === image.src
          ? editorImages[index]
          : editorImages.find(candidate => candidate.src === image.src) || editorImages[index])
        : undefined;
      image.style.setProperty('max-width', '100%');

      this.copyRenderedImageDimensions(image, editorImage);

      if (image.classList.contains('fr-fil')) {
        image.style.setProperty('float', 'left');
      }

      if (image.classList.contains('fr-fir')) {
        image.style.setProperty('float', 'right');
      }

      if (image.classList.contains('fr-dii')) {
        image.style.setProperty('display', 'inline-block');
      }

      if (image.classList.contains('fr-dib')) {
        image.style.setProperty('display', 'block');
      }

      if (editorImage) {
        // Match the editor's exact rendered margin instead of a hardcoded value.
        const computedStyle = getComputedStyle(editorImage);
        image.style.setProperty('margin', `${computedStyle.marginTop} ${computedStyle.marginRight} ${computedStyle.marginBottom} ${computedStyle.marginLeft}`);
      } else if (image.classList.contains('fr-fil')) {
        image.style.setProperty('margin', '5px 15px 5px 0');
      } else if (image.classList.contains('fr-fir')) {
        image.style.setProperty('margin', '5px 0 5px 15px');
      } else if (image.classList.contains('fr-dii')) {
        image.style.setProperty('margin', '5px');
      } else if (image.classList.contains('fr-dib')) {
        image.style.setProperty('margin', '5px auto');
      }
    });
  }

  private inlineSvgStyles(container: HTMLElement): void {
    const editorSvgs = Array.from(this.editor?.el?.querySelectorAll?.('svg') || []) as SVGSVGElement[];

    container.querySelectorAll('svg').forEach((svg: SVGSVGElement, index: number) => {
      const editorSvg = editorSvgs[index];
      const svgRect = editorSvg?.getBoundingClientRect?.();

      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

      if (svgRect?.width) {
        const width = Math.round(svgRect.width);
        svg.setAttribute('width', String(width));
        svg.style.setProperty('width', `${width}px`);
      }

      if (svgRect?.height) {
        const height = Math.round(svgRect.height);
        svg.setAttribute('height', String(height));
        svg.style.setProperty('height', `${height}px`);
      }

      this.copySvgPresentationStyles(svg, editorSvg);

      const svgElements = Array.from(svg.querySelectorAll('*')) as SVGElement[];
      const editorSvgElements = Array.from(editorSvg?.querySelectorAll?.('*') || []) as SVGElement[];

      svgElements.forEach((element, elementIndex) => {
        this.copySvgPresentationStyles(element, editorSvgElements[elementIndex]);
      });
    });
  }

  private copySvgPresentationStyles(targetElement: SVGElement, sourceElement?: SVGElement): void {
    if (!sourceElement) {
      return;
    }

    const computedStyle = getComputedStyle(sourceElement);
    this.setSvgPresentationValue(targetElement, 'fill', computedStyle.fill);
    this.setSvgPresentationValue(targetElement, 'stroke', computedStyle.stroke);
    this.setSvgPresentationValue(targetElement, 'color', computedStyle.color);
    this.setSvgPresentationValue(targetElement, 'opacity', computedStyle.opacity);
    this.setSvgPresentationValue(targetElement, 'stroke-width', computedStyle.strokeWidth);
    this.setSvgPresentationValue(targetElement, 'stroke-linecap', computedStyle.strokeLinecap);
    this.setSvgPresentationValue(targetElement, 'stroke-linejoin', computedStyle.strokeLinejoin);
  }

  private setSvgPresentationValue(targetElement: SVGElement, property: string, value: string): void {
    if (!value || value === 'none' || value === 'normal' || value === 'auto') {
      return;
    }

    targetElement.setAttribute(property, value);
    targetElement.style.setProperty(property, value);
  }

  private inlineEmojiIcons(container: HTMLElement): void {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => this.containsEmoji(node.textContent || '')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT
    });

    let currentNode = walker.nextNode();
    while (currentNode) {
      textNodes.push(currentNode as Text);
      currentNode = walker.nextNode();
    }

    textNodes.forEach(textNode => this.replaceEmojiTextNode(textNode));
  }

  private containsEmoji(text: string): boolean {
    return this.getEmojiPattern().test(text);
  }

  private replaceEmojiTextNode(textNode: Text): void {
    const text = textNode.textContent || '';
    const emojiPattern = this.getEmojiPattern();
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match = emojiPattern.exec(text);

    while (match) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
      }

      fragment.appendChild(this.createEmojiImage(match[0], textNode.parentElement as HTMLElement));
      lastIndex = emojiPattern.lastIndex;
      match = emojiPattern.exec(text);
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  }

  private createEmojiImage(emoji: string, parentElement?: HTMLElement): HTMLImageElement {
    const fontSize = this.getEmojiFontSize(parentElement);
    const imageSize = Math.ceil(fontSize * 1.35);
    const scale = window.devicePixelRatio || 2;
    const canvas = document.createElement('canvas');
    canvas.width = imageSize * scale;
    canvas.height = imageSize * scale;

    const context = canvas.getContext('2d');
    if (context) {
      context.scale(scale, scale);
      context.font = `${fontSize}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(emoji, imageSize / 2, imageSize / 2);
    }

    const image = document.createElement('img');
    image.src = canvas.toDataURL('image/png');
    image.alt = emoji;
    image.setAttribute('data-emoji', emoji);
    image.setAttribute('width', String(imageSize));
    image.setAttribute('height', String(imageSize));
    image.style.setProperty('display', 'inline-block');
    image.style.setProperty('width', `${imageSize}px`);
    image.style.setProperty('height', `${imageSize}px`);
    image.style.setProperty('vertical-align', '-0.2em');
    image.style.setProperty('margin', '0 2px');
    return image;
  }

  private getEmojiFontSize(element?: HTMLElement): number {
    let currentElement = element;

    while (currentElement) {
      const fontSize = this.getPixelSize(currentElement.style.fontSize || currentElement.getAttribute('font-size'));
      if (fontSize > 0) {
        return fontSize;
      }

      currentElement = currentElement.parentElement;
    }

    return 16;
  }

  private getEmojiPattern(): RegExp {
    // Only rasterize GENUINE pictographic emoji so that Froala "special
    // characters" (©, ®, ™, arrows, music/weather symbols, etc.) are preserved
    // as selectable Unicode text.
    //
    //  - Astral-plane characters (\uD83C-\uD83E surrogate pairs) are true color
    //    emoji and are always matched.
    //  - BMP symbols that overlap with plain text glyphs are matched ONLY when
    //    explicitly followed by the emoji variation selector \uFE0F. A bare ©
    //    (U+00A9) therefore stays as text, while an emoji-presentation ©️
    //    (U+00A9 U+FE0F) is still treated as an emoji.
    const bmpEmoji = '[\\u00A9\\u00AE\\u203C\\u2049\\u2122\\u2139\\u2194-\\u21AA\\u231A-\\u231B\\u2328\\u23CF\\u23E9-\\u23F3\\u23F8-\\u23FA\\u24C2\\u25AA-\\u25AB\\u25B6\\u25C0\\u25FB-\\u25FE\\u2600-\\u27BF\\u2934-\\u2935\\u2B05-\\u2B55]';
    const astralEmoji = '(?:\\uD83C[\\uD000-\\uDFFF]|\\uD83D[\\uDC00-\\uDEFF]|\\uD83E[\\uDD00-\\uDDFF])';
    // A single emoji unit: BMP requires the \uFE0F variation selector; astral
    // characters are emoji by default (variation selector optional).
    const emojiUnit = `(?:${bmpEmoji}\\uFE0F|${astralEmoji}\\uFE0F?)`;
    // Allow ZWJ (\u200D) sequences such as family/profession emoji.
    return new RegExp(`${emojiUnit}(?:\\u200D${emojiUnit})*`, 'g');
  }

  private copyRenderedImageDimensions(image: HTMLImageElement, editorImage?: HTMLImageElement): void {
    const renderedWidth = editorImage?.offsetWidth
      || this.getPixelSize(editorImage?.style.width || editorImage?.getAttribute('width') || image.style.width || image.getAttribute('width'))
      || editorImage?.getBoundingClientRect?.().width;
    const renderedHeight = editorImage?.offsetHeight
      || this.getPixelSize(editorImage?.style.height || editorImage?.getAttribute('height') || image.style.height || image.getAttribute('height'))
      || editorImage?.getBoundingClientRect?.().height;

    if (renderedWidth > 0) {
      const width = Math.round(renderedWidth);
      image.setAttribute('width', String(width));
      image.style.setProperty('width', `${width}px`);
    }

    if (renderedHeight > 0) {
      const height = Math.round(renderedHeight);
      image.setAttribute('height', String(height));
      image.style.setProperty('height', `${height}px`);
    }

    if (editorImage) {
      const computedStyle = getComputedStyle(editorImage);
      image.style.setProperty('vertical-align', computedStyle.verticalAlign || 'baseline');
      if (computedStyle.objectFit && computedStyle.objectFit !== 'fill') {
        image.style.setProperty('object-fit', computedStyle.objectFit);
      }
    }
  }

  private getPixelSize(value: string | null): number {
    if (!value) {
      return 0;
    }

    const match = String(value).match(/([0-9.]+)px|^([0-9.]+)$/);
    return match ? Number(match[1] || match[2]) : 0;
  }

  private getMeasuredReportContentHeight(content: string): number {
    const measurementRoot = document.createElement('div');
    const reportContent = document.createElement('div');

    measurementRoot.style.position = 'absolute';
    measurementRoot.style.visibility = 'hidden';
    measurementRoot.style.left = '-10000px';
    measurementRoot.style.top = '0';
    measurementRoot.style.width = `${this.A4_PAGE_WIDTH_PX}px`;
    measurementRoot.style.padding = `${this.A4_TOP_BOTTOM_MARGIN_PX}px ${this.A4_SIDE_MARGIN_PX}px`;
    measurementRoot.style.boxSizing = 'border-box';
    measurementRoot.style.pointerEvents = 'none';

    reportContent.className = 'fr-view';
    reportContent.innerHTML = this.prepareContentForA4Measurement(content);
    reportContent.style.width = `${this.reportPrintableWidth}px`;
    reportContent.style.maxWidth = `${this.reportPrintableWidth}px`;
    reportContent.style.minHeight = '0';
    reportContent.style.maxHeight = 'none';
    reportContent.style.height = 'auto';
    reportContent.style.padding = '0';
    reportContent.style.margin = '0';
    reportContent.style.boxSizing = 'border-box';
    reportContent.style.display = 'flow-root';
    reportContent.style.wordWrap = 'break-word';
    reportContent.style.overflow = 'visible';

    measurementRoot.appendChild(reportContent);
    document.body.appendChild(measurementRoot);

    const height = this.getRenderedReportContentHeight(reportContent);
    document.body.removeChild(measurementRoot);
    return height;
  }

  private getRenderedReportContentHeight(root: HTMLElement): number {
    const rootRect = root.getBoundingClientRect();
    let bottom = 0;

    const includeRect = (rect: DOMRect | ClientRect): void => {
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return;
      }

      bottom = Math.max(bottom, rect.bottom - rootRect.top);
    };

    const elements = Array.from(root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, table, img, svg, hr, iframe, video')) as HTMLElement[];
    elements.forEach((element) => {
      if ((element.textContent || '').replace(/\u00a0/g, ' ').trim() || element.querySelector('br, img, svg, hr, iframe, video') || ['TABLE', 'IMG', 'SVG', 'HR', 'IFRAME', 'VIDEO'].includes(element.tagName)) {
        includeRect(element.getBoundingClientRect());
      }
    });

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      if ((node.textContent || '').replace(/\u00a0/g, ' ').trim()) {
        const range = document.createRange();
        range.selectNodeContents(node);
        Array.from(range.getClientRects()).forEach(includeRect);
        range.detach();
      }

      node = walker.nextNode();
    }

    return Math.ceil(Math.max(0, bottom));
  }

  private getReportSafePageHeight(): number {
    return this.maxPageHeight + this.A4_MEASUREMENT_TOLERANCE_PX;
  }

  private prepareContentForA4Measurement(content: string): string {
    if (!content) {
      return content;
    }

    return this.prepareStaticContentForReport(content, true);
  }

  /**
   * Escape HTML special characters for safe insertion into HTML attributes/content
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Reset component state for clean reinitialization
   */
  private resetComponentState(): void {
    // Reset editor state
    this.editor = null;
    this.editorInitialized = false;
    this.initialPageContentLoaded = false;
    
    // Reset page management
    this.pages = [];
    this.activePage = 1;
    this.nextPageId = 1;
    
    // Reset save management
    this.saveQueue.clear();
    this.isSaving = false;
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    
    // Reset page switching
    this.isPageSwitching = false;
    this.switchingLock = false;
    this.pageSwitchBlocked = false;
    this.hasUnsavedContent = false;
    
    // Reset content management
    this.isUserTyping = false;
    this.preventAutoSave = false;
    this.allowTyping = true;
    this.contentHeight = 0;
    
    // Reset copy-paste protection
    this.lastKnownContent = '';
    this.clipboardContent = '';
    this.preventAutoPaste = false;
    this.a4StatusMessage = '';
    if (this.a4StatusMessageTimer) {
      clearTimeout(this.a4StatusMessageTimer);
      this.a4StatusMessageTimer = null;
    }
    this.promptedLegacyA4OverflowPageKeys.clear();
    
    // Reset image upload tracking
    this.imageUploadInProgress = false;
    this.pastedImageUploadInProgress = false;
    this.imageUploadQueue.clear();
    
    // Reset A4 size warning throttling
    this.lastA4WarningTime = 0;
  }

  ngOnInit() {
    // Reset component state in case of reinitialization
    this.resetComponentState();

    // Guard router navigation (side menu, browser back, etc.) so unsaved
    // changes trigger the same Save / Discard / Cancel prompt as page switch.
    this.unsavedChangesService.registerGuard(this.unsavedChangesGuardHandler);

    this.templateForm = this.fb.group({
      name: ['', [Validators.required]],
      langauge: ['', [Validators.required]],
    });

    setTimeout(() => {
      this.initializeTemplate();
      if (this.editorInitialized && !this.initialPageContentLoaded) {
        this.loadCurrentPageContent();
      }
    }, 200);
  }

  /**
   * Handler consulted by the route guard before leaving the editor via the
   * router. Reuses the centralized unsaved-changes check.
   */
  private unsavedChangesGuardHandler = (): Promise<boolean> => {
    return this.ensureCurrentPageSaved();
  };

  ngAfterViewInit() {
    // Add global reference for debugging and for event handlers
    (window as any).froalaDebug = this;
    (window as any).froalaComponent = this; // For use in Froala event handlers
    
    // Initialize ruler
    this.generateRulerMarks();
    this.setupRulerScrollSync();
    this.applyA4Scale();
    this.observeA4ScaleChanges();
    this.patchFroalaPopupContainerRouting();
    
    // Check if editor is ready after view initialization
    setTimeout(() => {
      // If editor still not ready, try to force initialization
      if (!this.editorInitialized && this.editor) {
        this.waitForEditorMethods();
      }
    }, 1000);

    this.enableLanguageClearKeyboardNavigation();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.scheduleA4ScaleUpdate();
  }

  /**
   * Coalesce rapid resize events into a single scale update per frame.
   */
  private scheduleA4ScaleUpdate(): void {
    if (this.a4ScaleFrameId !== null) {
      cancelAnimationFrame(this.a4ScaleFrameId);
    }

    this.a4ScaleFrameId = requestAnimationFrame(() => {
      this.a4ScaleFrameId = null;
      this.applyA4Scale();
    });
  }

  /**
   * Apply the current visual zoom while keeping the underlying A4 canvas fixed.
   */
  private applyA4Scale(): void {
    const stage = this.a4ZoomStage?.nativeElement;
    const canvas = this.a4ScaleCanvas?.nativeElement;

    if (!stage || !canvas) {
      return;
    }

    this.a4Scale = this.clampA4Scale(this.a4Scale);
    const appliedScale = this.isFullscreenMode ? this.defaultA4Scale : this.a4Scale;

    canvas.style.transform = `scale(${appliedScale})`;
    const canvasHeight = Math.max(canvas.offsetHeight || 0, canvas.scrollHeight || 0, this.A4_CANVAS_HEIGHT_PX * this.minA4Scale);
    stage.style.width = this.isFullscreenMode ? '100%' : `${this.A4_CANVAS_WIDTH_PX * appliedScale}px`;
    stage.style.height = `${canvasHeight * appliedScale}px`;
  }

  private syncFullscreenState(): void {
    const fullscreenPlugin = this.editor?.fullscreen;
    const nextFullscreenState = typeof fullscreenPlugin?.isActive === 'function'
      ? !!fullscreenPlugin.isActive()
      : !!document.querySelector('.fr-box.fr-fullscreen');

    if (this.isFullscreenMode === nextFullscreenState) {
      return;
    }

    this.isFullscreenMode = nextFullscreenState;
    this.scheduleA4ScaleUpdate();
  }

  public zoomInA4(): void {
    this.setA4Scale(this.a4Scale + this.a4ScaleStep);
  }

  public zoomOutA4(): void {
    this.setA4Scale(this.a4Scale - this.a4ScaleStep);
  }

  public resetA4Zoom(): void {
    this.setA4Scale(this.defaultA4Scale);
  }

  public get a4ZoomPercent(): number {
    return Math.round(this.a4Scale * 100);
  }

  private setA4Scale(scale: number): void {
    this.a4Scale = this.clampA4Scale(scale);
    this.applyA4Scale();
  }

  private clampA4Scale(scale: number): number {
    const roundedScale = Number(scale.toFixed(2));
    return Math.min(this.maxA4Scale, Math.max(this.minA4Scale, roundedScale));
  }

  /**
   * Keep the zoom stage footprint in sync when the fixed A4 canvas height changes.
   */
  private observeA4ScaleChanges(): void {
    const canvas = this.a4ScaleCanvas?.nativeElement;

    if (!canvas || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.a4ScaleObserver?.disconnect();
    this.a4ScaleObserver = new ResizeObserver(() => this.scheduleA4ScaleUpdate());
    this.a4ScaleObserver.observe(canvas);
  }

  /**
   * Toggle ruler visibility
   */
  public toggleRuler(): void {
    this.showRuler = !this.showRuler;
    if (this.showRuler) {
      setTimeout(() => {
        this.generateRulerMarks();
        this.setupRulerScrollSync();
      }, 100);
    } else {
      // Clear guides when ruler is hidden
      this.hoverGuidePosition = null;
      this.isHoveringRuler = false;
    }

    this.scheduleA4ScaleUpdate();
  }

  /**
   * Handle mouse move over ruler to show hover guide
   */
  public onRulerMouseMove(event: MouseEvent): void {
    const rulerElement = event.currentTarget as HTMLElement;
    const rect = rulerElement.getBoundingClientRect();
    const scrollLeft = rulerElement.scrollLeft || 0;
    
    // Calculate position relative to ruler content (accounting for scroll).
    // The rect is already scaled, so convert back to unscaled canvas pixels.
    this.hoverGuidePosition = (event.clientX - rect.left) / this.a4Scale + scrollLeft;
    this.isHoveringRuler = true;
  }

  /**
   * Handle mouse leave from ruler
   */
  public onRulerMouseLeave(): void {
    this.hoverGuidePosition = null;
    this.isHoveringRuler = false;
  }

  /**
   * Handle click on ruler to add/remove persistent guide
   */
  public onRulerClick(event: MouseEvent): void {
    const rulerElement = event.currentTarget as HTMLElement;
    const rect = rulerElement.getBoundingClientRect();
    const scrollLeft = rulerElement.scrollLeft || 0;
    
    const clickPosition = (event.clientX - rect.left) / this.a4Scale + scrollLeft;
    
    // Check if clicking near an existing guide (within 5px tolerance)
    const existingGuideIndex = this.persistentGuides.findIndex(
      guide => Math.abs(guide - clickPosition) < 5
    );
    
    if (existingGuideIndex !== -1) {
      // Remove existing guide
      this.persistentGuides.splice(existingGuideIndex, 1);
    } else {
      // Add new guide
      this.persistentGuides.push(clickPosition);
    }
  }

  /**
   * Remove a specific persistent guide
   */
  public removeGuide(position: number): void {
    const index = this.persistentGuides.indexOf(position);
    if (index !== -1) {
      this.persistentGuides.splice(index, 1);
    }
  }

  /**
   * Clear all persistent guides
   */
  public clearAllGuides(): void {
    this.persistentGuides = [];
  }

  /**
   * Generate ruler marks based on unit and actual editor width
   */
  private generateRulerMarks(): void {
    this.rulerMarks = [];
    
    // Get actual editor width from DOM
    const editorWrapper = document.querySelector('.fr-wrapper') as HTMLElement;
    const editorElement = document.querySelector('.fr-element') as HTMLElement;
    
    let actualWidth = this.A4_PAGE_WIDTH_PX; // A4 width at 96 DPI
    
    if (editorWrapper) {
      actualWidth = editorWrapper.offsetWidth;
    } else if (editorElement) {
      actualWidth = editorElement.offsetWidth;
    }
    
    // Calculate max width in inches based on actual editor width
    const maxWidthInches = actualWidth / this.DPI;
    
    if (this.rulerUnit === 'inch') {
      // Generate marks for inches based on actual width
      for (let i = 0; i <= Math.ceil(maxWidthInches); i++) {
        const position = i * this.DPI;
        
        // Major mark - full inch with label
        this.rulerMarks.push({
          position: position,
          label: i.toString(),
          isMajor: true,
          type: 'major'
        });
        
        // Generate 16 subdivisions for more precision (1/16 inch marks)
        for (let j = 1; j < 16; j++) {
          const subdivisionPosition = position + (j * this.DPI / 16);
          
          if (subdivisionPosition < actualWidth) {
            // Determine mark type based on position
            if (j % 4 === 0) {
              // Quarter marks (4/16, 8/16, 12/16) - medium height
              this.rulerMarks.push({
                position: subdivisionPosition,
                isMajor: false,
                type: 'medium'
              });
            } else if (j % 2 === 0) {
              // Eighth marks (2/16, 6/16, 10/16, 14/16) - shorter
              this.rulerMarks.push({
                position: subdivisionPosition,
                isMajor: false,
                type: 'minor'
              });
            } else {
              // Sixteenth marks (odd numbers) - smallest
              this.rulerMarks.push({
                position: subdivisionPosition,
                isMajor: false,
                type: 'minor'
              });
            }
          }
        }
      }
    } else if (this.rulerUnit === 'cm') {
      // Generate marks for centimeters
      const cmPerInch = 2.54;
      const maxCm = maxWidthInches * cmPerInch;
      
      for (let i = 0; i <= Math.ceil(maxCm); i++) {
        const position = (i / cmPerInch) * this.DPI;
        
        // Major mark - full cm with label
        this.rulerMarks.push({
          position: position,
          label: i.toString(),
          isMajor: true,
          type: 'major'
        });
        
        // Generate 10 subdivisions (millimeters)
        for (let j = 1; j < 10; j++) {
          const mmPosition = position + (j * this.DPI / cmPerInch / 10);
          
          if (mmPosition < actualWidth) {
            if (j === 5) {
              // Half cm mark (5mm) - medium height
              this.rulerMarks.push({
                position: mmPosition,
                isMajor: false,
                type: 'medium'
              });
            } else {
              // Millimeter marks - small
              this.rulerMarks.push({
                position: mmPosition,
                isMajor: false,
                type: 'minor'
              });
            }
          }
        }
      }
    }
  }

  /**
   * Setup ruler scroll synchronization with editor
   */
  private setupRulerScrollSync(): void {
    setTimeout(() => {
      const editorElement = document.querySelector('.fr-element');
      const rulerElement = document.querySelector('.ruler');
      const editorWrapper = document.querySelector('.fr-wrapper') as HTMLElement;
      const editorScrollElement = editorWrapper || editorElement;
      
      if (editorScrollElement && rulerElement) {
        // Sync horizontal scroll
        editorScrollElement.addEventListener('scroll', () => {
          (rulerElement as HTMLElement).scrollLeft = (editorScrollElement as HTMLElement).scrollLeft;
        });
      }
      
      // Setup resize observer to regenerate ruler marks when editor width changes
      if (editorWrapper && typeof ResizeObserver !== 'undefined') {
        // Clean up existing observer if any
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        
        this.resizeObserver = new ResizeObserver(() => {
          // Debounce the regeneration
          setTimeout(() => {
            this.generateRulerMarks();
          }, 100);
        });
        
        this.resizeObserver.observe(editorWrapper);
      }
    }, 500);
  }

  /**
   * Initialize template data and pages
   */
  private initializeTemplate(): void {
    if (this.templateData) {
      this.templateName = this.templateData?.file_name;
      this.languages.forEach((lang) => {
        if (lang.label === this.templateData?.language || lang.value === this.templateData?.language) {
          this.selectedLangauge = lang.value;
        }
      });
      this.templateId = this.templateData?.id;

      // Initialize pages from template data
      this.initializePagesFromData();
    } else {
      // Create first page if no template data
      this.createFirstPage();
    }
  }

  /**
   * Initialize pages from template data
   */
  private initializePagesFromData(): void {
    const pageList = Array.isArray(this.templateData?.page_list) ? this.templateData.page_list : [];

    if (pageList.length > 0) {
      const usedPageNumbers = new Set<number>();
      const sortedPageList = [...pageList].sort((firstPage, secondPage) => {
        return this.resolvePageNumber(firstPage) - this.resolvePageNumber(secondPage);
      });

      this.pages = sortedPageList.map((pageData, index) => {
        let pageNumber = this.resolvePageNumber(pageData);

        if (!pageNumber || usedPageNumbers.has(pageNumber)) {
          pageNumber = index + 1;
          while (usedPageNumbers.has(pageNumber)) {
            pageNumber++;
          }
        }

        usedPageNumbers.add(pageNumber);

        return {
          id: pageNumber,
          title: `Page ${pageNumber}`,
          content: pageData.static_content || '',
          pagesId: pageData.static_template_pages_id || '',
          isDirty: false,
          savedContent: pageData.static_content || ''
        };
      });

      this.activePage = this.pages[0]?.id || 1;
      this.nextPageId = Math.max(...this.pages.map(page => page.id), 0) + 1;
    } else {
      this.createFirstPage();
    }
  }

  private resolvePageNumber(pageData: any): number {
    const pageNumber = Number(pageData?.page_no);
    return Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 0;
  }

  /**
   * Create the first page
   */
  private createFirstPage(): void {
    const firstPage: Page = {
      id: 1,
      title: 'Page 1',
      content: '',
      pagesId: '',
      isDirty: false,
      savedContent: ''
    };
    this.pages = [firstPage];
    this.activePage = 1;
    this.nextPageId = 2;
  }

  /**
   * Setup Froala editor configuration options
   */
  private setupEditorOptions(): void {
    this.patchFroalaImageAlignCommand();

    const loggedInUserId = this.userInfo?.userId;
    
    // Get authentication token for image uploads
    const authToken = this.userInfo?.token || this.userInfo?.accessToken || 
                     localStorage.getItem('authToken') || localStorage.getItem('access_token');
    
    // Prepare request headers with authentication
    const requestHeaders: any = {
      'Accept': 'application/json'
    };
    
    // Add authentication token if available
    if (authToken) {
      requestHeaders['Authorization'] = `Bearer ${authToken}`;
    }
    
    this.options = {
      key: Froala_Constants.LICENSE_KEY,
      language: this.getFroalaLanguage(),
      attribution: false,
      // Keep Froala popup math in the same unscaled coordinate space as the
      // visible scroll viewport. The A4 canvas itself is transform-scaled.
      scrollableContainer: `#${this.froalaPopupViewportId}`,
      html2pdf: html2pdf,
      fontFamilyDefaultSelection: 'var(--font-NeueHaasDisplayLight)',
      fontFamily: {
        'Neue Haas Display Light': 'var(--font-NeueHaasDisplayLight)',
      },
      fontSizeDefaultSelection: '12',
      charCounterCount: false,
      wordCounterCount: true,
      wordCounterMax: -1,
      placeholderText: this.translate.instant('create_your_template_here'),
      pastePlain: false,
      pasteDeniedTags: ['meta', 'style', 'script', 'link'],
      enter: typeof FroalaEditor !== 'undefined' && FroalaEditor?.ENTER_P ? FroalaEditor.ENTER_P : 1,
      imageUpload: true,
      imageDefaultAlign: 'left',
      imageDefaultDisplay: 'inline',
      imageMaxSize: 5 * 1024 * 1024,
      imageAllowedTypes: ['jpeg', 'jpg', 'png'],
      imageUploadParam: 'image_param',
      imageUploadURL: urlPathConstants.imageUploadUrl,
      imageUploadMethod: 'POST',
      imageUploadParams: {
        static_template_img_id: 0,
        temp_id: `temp_${Date.now()}`,
        created_by: loggedInUserId,
        is_active: true
      },
      requestWithCORS: false,
      requestHeaders: requestHeaders,
      imageCORSProxy: '',
      imageAddNewLine: true,
      imageDefaultMargin: 5,
      imagePaste: true,
      imagePasteProcess: true,
      imageMove: true,
      imageResize: true,
      // Force image upload for pasted images
      imageUploadToS3: false,
      imageEditButtons: ['imageReplace', 'imageAlign', 'imageRemove'],
      quickInsertButtons: ['image', 'table', 'ul', 'ol', 'hr'],
      toolbarButtons: {
        moreText: {
          buttons: ['getPDF', 'bold', 'italic', 'underline', 'textColor', 'specialCharacters', 'subscript', 'superscript', 'strikeThrough', 'fontSize', 'backgroundColor'],
          buttonsVisible: 5
        },
        moreParagraph: {
          buttons: ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'formatOL', 'formatUL', 'paragraphFormat', 'outdent', 'indent', 'lineHeight'],
          buttonsVisible: 5
        },
        moreRich: {
          buttons: ['insertLink', 'insertImage', 'insertTable'],
          buttonsVisible: 5
        },
        moreMisc: {
          buttons: ['undo', 'redo', 'fullscreen'],
          align: 'right',
          buttonsVisible: 3
        }
      },
      colorsStep: 6,
      colorsText: [
        '#000000', '#ffffff', '#00377B', '#06B5CD', '#61C0CE', '#C2D0D7'
      ],
      colorsBackground: [
        '#000000', '#ffffff', '#00377B', '#06B5CD', '#61C0CE', '#C2D0D7', 'REMOVE',
      ],
      tableStyles: {
        noborder: 'Remove Border',
        addborder: 'Add Border',
        dashedborder: 'Dashed Border'
      },
      tableCellMultipleStyles: false,
      listAdvancedTypes: false,
      imageInsertButtons: ['imageUpload'],
      imageManager: false,
      events: {
        'paste.afterCleanup': (clipboardHtml: string) => this.normalizePastedBlockStructure(clipboardHtml),

        'image.beforeUpload': function(images) {
          // Get editor reference from 'this' context (Froala editor instance)
          const editor = this; // 'this' refers to the Froala editor instance in event handlers
          const component = (window as any).froalaComponent; // Get component reference

          if (images && images.length) {
            const image = images[0];
            
            // Validate image size
            if (image.size > 5 * 1024 * 1024) {
              if (component && component.toastService) {
                component.toastService.presentToast(component.translate.instant('file_size_exceed_5mb'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
              }
              return false;
            }
            
            // Validate image type
            if (!['image/jpeg', 'image/jpg', 'image/png'].includes(image.type)) {
              if (component && component.toastService) {
                component.toastService.presentToast(component.translate.instant('image_file_types_allowed_msg'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
              }
              return false;
            }
            
            // Set upload parameters using the editor context
            if (editor && editor.opts) {
              // Generate proper filename for pasted images (which don't have a name property)
              const fileName = image.name || `pasted-image-${Date.now()}.${image.type.split('/')[1] || 'png'}`;
              
              // Use the exact same pattern as the working editor
              editor.opts.imageUploadParams = {
                static_template_img_id: 0,
                file_name: fileName,
                temp_id: Date.now(), // Simple timestamp like working version
                page_no: component ? component.activePage : 1,
                file_size: image.size,
                created_by: loggedInUserId,
                is_active: true
              };
              
              // Mark image upload as in progress to prevent premature content saving
              if (component) {
                const uploadId = `upload_${Date.now()}_${Math.random()}`;
                component.imageUploadQueue.add(uploadId);
                
                // Detect if this is a pasted image (no name) vs file picker (has name)
                const isPastedImage = !image.name;
                if (isPastedImage) {
                  component.pastedImageUploadInProgress = true;
                  component.imageUploadInProgress = true;
                } else {
                  // File picker uploads don't need content blocking
                  console.log('📁 File picker image upload started. Upload ID:', uploadId);
                }
                
                // Store upload info for tracking
                editor.opts.imageUploadParams.upload_id = uploadId;
                editor.opts.imageUploadParams.is_pasted = isPastedImage;
              }
              
              // Add debugging to track when the upload actually starts
              console.log('🚀 Returning true - Froala should now make HTTP request...');
              
              return true;
            } else {
              // Try fallback using component reference
              if (component && component.editor && component.editor.opts) {
                // Generate proper filename for pasted images
                const fileName = image.name || `pasted-image-${Date.now()}.${image.type.split('/')[1] || 'png'}`;
                
                component.editor.opts.imageUploadParams = {
                  static_template_img_id: 0,
                  file_name: fileName,
                  temp_id: Date.now(),
                  page_no: component.activePage,
                  file_size: image.size,
                  created_by: loggedInUserId,
                  is_active: true
                };
                
                return true;
              }
              
              return false;
            }
          }
          
          return false;
        },
        
        'image.uploaded': function(response) {
          // Try to parse the response to get the image URL
          let imageUrl = null;
          let parsedResponse = null;
          
          try {
            // Parse response if it's a string
            if (typeof response === 'string') {
              parsedResponse = JSON.parse(response);
            } else {
              parsedResponse = response;
            }
            
            // Extract image URL from different possible response structures
            if (parsedResponse) {
              // Common response patterns for image upload APIs
              imageUrl = parsedResponse.link || 
                        parsedResponse.url || 
                        parsedResponse.image_url || 
                        parsedResponse.file_url ||
                        parsedResponse.data?.url ||
                        parsedResponse.data?.link ||
                        parsedResponse.response?.url ||
                        parsedResponse.response?.link ||
                        parsedResponse.response?.image_url;
            }
            
            // If we found an image URL, return it to Froala
            if (imageUrl) {
              // Validate the URL and check for common issues
              if (imageUrl.includes('/undefined')) {
                // Try to fix the URL by removing "/undefined" if possible
                const fixedUrl = imageUrl.replace('/undefined', '');
                if (fixedUrl !== imageUrl && fixedUrl.length > 10) {
                  imageUrl = fixedUrl;
                }
              }
              
              // Additional URL validation
              try {
                const urlObj = new URL(imageUrl);
                console.log('URL validation passed:', {
                  protocol: urlObj.protocol,
                  hostname: urlObj.hostname,
                  pathname: urlObj.pathname,
                  search: urlObj.search
                });
              } catch (urlError) {
                console.error('❌ Invalid URL format:', urlError);
                
                const component = (window as any).froalaComponent;
                if (component && component.toastService) {
                  component.toastService.presentToast(component.translate.instant('upload_failed'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
                }
                return; // Don't return invalid URL to Froala
              }
              
              // Froala expects the response to have a 'link' property
              // Return the proper response structure
              const froalaResponse = {
                link: imageUrl
              };
              
              // Force update the editor content to ensure images are replaced properly
              setTimeout(() => {
                try {
                  const editor = this; // 'this' is the Froala editor instance
                  const component = (window as any).froalaComponent;
                  
                  if (editor && editor.el && component) {
                    const uploadId = parsedResponse?.upload_id;
                    const isPastedImage = parsedResponse?.is_pasted;
                    
                    // Only do manual replacement for pasted images
                    if (isPastedImage) {
                      // Find any remaining data URI images and replace them
                      const remainingDataImages = editor.el.querySelectorAll('img[src^="data:"]');
                      if (remainingDataImages.length > 0) {
                        // Try to manually replace them with the uploaded URL
                        remainingDataImages.forEach((img, index) => {
                          // Replace with blob URL
                          img.src = imageUrl;
                          
                          // Copy over the upload metadata attributes to match file picker behavior
                          if (parsedResponse) {
                            img.setAttribute('base64_image_string', 'null');
                            if (parsedResponse.static_template_img_id !== undefined) {
                              img.setAttribute('static_template_img_id', String(parsedResponse.static_template_img_id));
                            }
                            if (parsedResponse.temp_id) img.setAttribute('temp_id', String(parsedResponse.temp_id));
                            if (parsedResponse.page_no) img.setAttribute('page_no', String(parsedResponse.page_no));
                            if (parsedResponse.file_name) img.setAttribute('file_name', parsedResponse.file_name);
                            if (parsedResponse.file_url) img.setAttribute('file_url', parsedResponse.file_url);
                            if (parsedResponse.file_size) img.setAttribute('file_size', String(parsedResponse.file_size));
                            if (parsedResponse.created_by) img.setAttribute('created_by', parsedResponse.created_by);
                            if (parsedResponse.is_active !== undefined) {
                              img.setAttribute('is_active', String(parsedResponse.is_active));
                            }
                            
                            // Add Froala classes that are added to file picker images
                            if (!img.classList.contains('fr-fil')) {
                              img.classList.add('fr-fil');
                            }
                            if (!img.classList.contains('fr-dii')) {
                              img.classList.add('fr-dii');
                            }
                          }
                        });
                      }
                    }
                    
                    // Clear upload in progress flags
                    if (uploadId && component.imageUploadQueue) {
                      component.imageUploadQueue.delete(uploadId);
                    }
                    
                    // Clear pasted image flag if this was a pasted image
                    if (isPastedImage) {
                      component.pastedImageUploadInProgress = false;
                      console.log('🏁 Pasted image upload completed, content saves no longer blocked for pasted images');
                    }
                    
                    if (component.imageUploadQueue && component.imageUploadQueue.size === 0) {
                      component.imageUploadInProgress = false;
                      console.log('🏁 All image uploads completed');
                    }
                    
                    // Only trigger manual content change for pasted images
                    if (isPastedImage && editor.html && typeof editor.html.get === 'function') {
                      const updatedContent = editor.html.get();
                      // Use a small delay to ensure DOM updates are complete
                      setTimeout(() => {
                        component.handleContentChange(updatedContent);
                      }, 50);
                    }
                  }
                } catch (replaceError) {
                  console.error('Error in upload completion processing:', replaceError);
                  
                  // Clear upload flags even on error
                  const component = (window as any).froalaComponent;
                  if (component) {
                    component.imageUploadInProgress = false;
                    component.pastedImageUploadInProgress = false;
                    component.imageUploadQueue.clear();
                  }
                }
              }, 100); // Small delay to let Froala process the response first
              
              const component = (window as any).froalaComponent;
              if (component && component.toastService) {
                component.toastService.presentToast(component.translate.instant('image_uploaded_successfully'), 2000, AppConstants.SUCCESS_SNACKBAR, 'top');
              }
              
              // Return the response with the image URL
              return froalaResponse;
            } else {
              const component = (window as any).froalaComponent;
              if (component && component.toastService) {
                component.toastService.presentToast(component.translate.instant('upload_failed'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
              }
            }
            
          } catch (parseError) {
            console.error('❌ Error parsing upload response:', parseError);
            console.log('Raw response that failed to parse:', response);
            
            const component = (window as any).froalaComponent;
            if (component && component.toastService) {
              component.toastService.presentToast(component.translate.instant('upload_failed'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          }
        },
        
        'image.error': function(error, response) {
          let errorMessage = 'Image upload failed. Please try again.';
          
          try {
            // Try to extract more specific error message
            if (response && typeof response === 'string') {
              const responseData = JSON.parse(response);
              if (responseData && responseData.message) {
                errorMessage = responseData.message;
              } else if (responseData && responseData.error) {
                errorMessage = responseData.error;
              }
            } else if (response && typeof response === 'object') {
              if (response.message) {
                errorMessage = response.message;
              } else if (response.error) {
                errorMessage = response.error;
              }
            } else if (error && error.message) {
              errorMessage = error.message;
            }
          } catch (parseError) {
            // Use default error message if parsing fails
          }
          
          // Clear upload in progress flags on error
          const component = (window as any).froalaComponent;
          if (component) {
            component.imageUploadInProgress = false;
            component.pastedImageUploadInProgress = false;
            if (component.imageUploadQueue) {
              component.imageUploadQueue.clear();
            }
          }
          
          if (component && component.toastService) {
            component.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, 'top');
          }
        },
        
        'image.inserted': function(img, response) {
          // Validate inserted image
          if (img && img.src) {
            if (img.src.startsWith('data:')) {
              // Image still has data URI - upload may have failed
            } else {
              // Test if the image can actually be loaded
              const testImg = new Image();
              testImg.onerror = function() {
                const component = (window as any).froalaComponent;
                if (component && component.toastService) {
                  component.toastService.presentToast(component.translate.instant('upload_failed'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
                }
              };
              testImg.src = img.src;
            }
          }
        },
        
        'image.replaced': function(img, response) {
          // Image replaced successfully  
        },
        
        'image.paste': function(img) {
          // Image pasted, imagePasteProcess will trigger upload
        },
        
        initialized: (editor) => {
          console.log('Froala editor initialized via directive');
          console.log('Received editor object:', {
            hasEditor: !!editor,
            hasEditorProperty: !!(editor && editor._editor),
            hasHtml: !!(editor && editor.html),
            hasNestedHtml: !!(editor && editor._editor && editor._editor.html),
            hasHtmlMethods: !!(editor && editor.html && typeof editor.html.get === 'function' && typeof editor.html.set === 'function'),
            hasNestedHtmlMethods: !!(editor && editor._editor && editor._editor.html && typeof editor._editor.html.get === 'function' && typeof editor._editor.html.set === 'function'),
            editorType: typeof editor
          });
          
          // If the editor has a nested _editor property, use that
          if (editor && editor._editor) {
            console.log('Using nested _editor property');
            this.editor = editor._editor;
          } else {
            console.log('Using editor directly');
            this.editor = editor;
          }

          this.patchFroalaPopupContainerRouting();
          this.patchFroalaImageAlignCommand();
          bindFroalaListIndentFix(this.editor);
          this.relabelImageAlignCenterOption();
          bindFroalaTableSelectionHandlers(this.editor);
          
          // Use the proper initialization flow that includes retries
          this.waitForEditorMethods();
        },
        
        'contentChanged': () => {
          // Sometimes contentChanged fires before initialized completes
          // Use this as a backup to ensure editor is ready
          if (!this.editorInitialized && this.editor) {
            this.waitForEditorMethods();
          }

          normalizeFroalaTableVerticalAlignCells(this.editor);
          
          // Only skip content change handling if pasted image uploads are in progress
          // File picker uploads should proceed normally
          if (this.pastedImageUploadInProgress) {
            return;
          }
          
          if (!this.isPageSwitching && !this.preventAutoPaste && this.editorInitialized && this.editor && this.editor.html) {
            try {
              const currentContent = this.editor.html.get();
              this.handleContentChange(currentContent);
            } catch (error) {
              console.error('Error handling content change:', error);
            }
          }
        },

        'image.resizeEnd': () => {
          if (this.isPageSwitching || this.preventAutoPaste || !this.editorInitialized || !this.editor?.html?.get) {
            return;
          }

          this.hasUserEditedSinceLoad = true;
          requestAnimationFrame(() => requestAnimationFrame(() => {
            if (this.editorInitialized && this.editor?.html?.get) {
              this.handleContentChange(this.editor.html.get());
            }
          }));
        },
        
        'paste.before': (e) => {
          if (this.isPageSwitching || this.preventAutoPaste || !this.allowTyping) {
            console.log('Paste prevented - page switching, auto-paste protection, or A4 lock active');
            if (!this.allowTyping && Date.now() - this.lastA4WarningTime > 2000) {
              this.toastService.presentToast(
                'Content exceeds A4 size. Only deletion is allowed.',
                3000,
                AppConstants.WARNING_SNACKBAR,
                'top'
              );
              this.lastA4WarningTime = Date.now();
            }
            e.preventDefault();
            return false;
          }

          // Check if clipboard contains images first
          if (e && e.clipboardData && e.clipboardData.items) {
            const items = e.clipboardData.items;
            
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              
              if (item.type.indexOf('image') !== -1) {
                // Allow the image paste to proceed and trigger upload
                // Store current content before paste
                if (this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
                  this.lastKnownContent = this.editor.html.get();
                }
                return true; // Allow paste to proceed
              }
            }
          }
          
          // Store current content before paste
          if (this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
            this.lastKnownContent = this.editor.html.get();
          }

          if (this.insertLargestFittingPaste(e)) {
            e.preventDefault();
            return false;
          }
          
          console.log('=== END PASTE EVENT ===');
        },
        
        'paste.after': () => {
          if (!this.isPageSwitching && !this.preventAutoPaste && this.editor && this.editor.html) {
            // Paste is a genuine user edit
            this.hasUserEditedSinceLoad = true;
            try {
              // Add a small delay to let imagePasteProcess work first
              setTimeout(() => {
                if (!this.editor || !this.editor.html) {
                  console.warn('Editor lost during paste processing');
                  return;
                }
                
                const currentContent = this.editor.html.get();
                const sanitizedContent = this.stripClipboardMetadataFromHtml(currentContent);
                if (sanitizedContent !== currentContent) {
                  this.preventAutoPaste = true;
                  this.editor.html.set(sanitizedContent);
                  this.preventAutoPaste = false;
                }

                const newContent = sanitizedContent;
                if (newContent !== this.lastKnownContent) {
                  console.log('Content changed after paste (with delay)');
                  
                  // Only check for pasted images if the content actually contains img tags
                  if (newContent.includes('<img')) {
                    // Check if images were pasted and need to be processed
                    const images = this.editor.el.querySelectorAll('img[src^="data:"]');
                    if (images.length > 0) {
                      console.log('🚨 Data URI images found after paste - setting up monitoring');
                      console.log('Number of data URI images:', images.length);
                      console.log('Editor initialized:', this.editorInitialized);
                      console.log('imagePasteProcess setting:', this.editor.opts?.imagePasteProcess);
                      console.log('imageUpload setting:', this.editor.opts?.imageUpload);
                      
                      // Check if this is the first time pasting on an empty page
                      const isEmptyPage = this.lastKnownContent.trim().length === 0;
                      if (isEmptyPage) {
                        console.log('📄 First paste on empty page - ensuring editor is fully ready');
                        
                        // Ensure editor is fully initialized before proceeding
                        if (!this.editorInitialized) {
                          console.warn('⚠️ Editor not fully initialized for first paste - waiting...');
                          setTimeout(() => {
                            if (this.editorInitialized) {
                              console.log('✅ Editor now initialized - re-checking paste content');
                              this.checkAndHandlePastedImages();
                            } else {
                              console.error('❌ Editor still not initialized - manual upload may be needed');
                              this.triggerImageUploadForPastedImages(images);
                            }
                          }, 1000);
                          return;
                        }
                      }
                      
                      // Set up monitoring for image upload process
                      this.monitorImageUploadProgress(images, 0);
                    }
                  } else {
                    console.log('Text content pasted (no images detected)');
                  }
                  // Handle content change normally
                  this.handleContentChange(newContent);
                }
              }, 500); // Initial delay to let imagePasteProcess start
            } catch (error) {
              console.error('Error handling paste:', error);
            }
          }
        },
        
        focus: () => {
          // Reset auto-paste protection on focus (with delay)
          setTimeout(() => {
            if (!this.isPageSwitching) {
              this.preventAutoPaste = false;
            }
          }, 100);
        },
        
        blur: () => {},

        'popups.show.image.edit': () => {
          this.pendingImageAlignElement = this.getImageAlignmentTarget(this.getCurrentImageElement());
          this.patchFroalaImageAlignCommand();
          this.relabelImageAlignCenterOption();
        },

        'popups.show.table.edit': () => {
          rememberCurrentFroalaTableCells(this.editor);
          normalizeFroalaTableVerticalAlignCells(this.editor);
        },

        'commands.before': (command: string, commandValue?: string) => {
          if (!this.allowTyping && !['getPDF', 'fullscreen', 'help'].includes(command)) {
            if (Date.now() - this.lastA4WarningTime > 2000) {
              this.toastService.presentToast(
                'Content exceeds A4 page limit. Please remove some content.',
                3000,
                AppConstants.WARNING_SNACKBAR,
                'top'
              );
              this.lastA4WarningTime = Date.now();
            }
            return false;
          }

          if (command === 'imageAlign') {
            this.pendingImageAlignElement = this.getImageAlignmentTarget(this.getCurrentImageElement());
          }

          if (['tableStyle', 'tableCellStyle', 'tableCellVerticalAlign'].includes(command)) {
            rememberCurrentFroalaTableCells(this.editor);
          }
          if (command === 'indent' || command === 'outdent') {
            const listIndentResult = handleFroalaListIndentCommand(this.editor, command);

            if (listIndentResult.handled) {
              if (listIndentResult.changed) {
                this.hasUserEditedSinceLoad = true;
                setTimeout(() => {
                  this.handleContentChange(this.editor.html.get());
                }, 0);
              }

              return false;
            }
          }

          if (command === 'tableStyle' && ['noborder', 'addborder', 'dashedborder'].includes(commandValue || '')) {
            const didApplyBorder = applyFroalaTableBorderStyleToSelection(this.editor, commandValue);

            if (didApplyBorder) {
              this.hasUserEditedSinceLoad = true;
              setTimeout(() => {
                this.handleContentChange(this.editor.html.get());
              }, 0);
              return false;
            }
          }

          if (command === 'tableCellStyle' && ['fr-highlighted', 'fr-thick'].includes(commandValue || '')) {
            const didApplyCellStyle = applyFroalaTableCellStyleToSelection(this.editor, commandValue);

            if (didApplyCellStyle) {
              this.hasUserEditedSinceLoad = true;
              setTimeout(() => {
                this.handleContentChange(this.editor.html.get());
              }, 0);
              return false;
            }
          }

          if (command === 'tableCellVerticalAlign' && ['top', 'middle', 'bottom'].includes(commandValue || '')) {
            const didApplyVerticalAlign = applyFroalaTableVerticalAlignToSelection(this.editor, commandValue);

            if (didApplyVerticalAlign) {
              this.hasUserEditedSinceLoad = true;
              setTimeout(() => {
                this.handleContentChange(this.editor.html.get());
              }, 0);
              return false;
            }
          }

        },

        'commands.mousedown': () => {
          rememberCurrentFroalaTableCells(this.editor);
        },

        'mousedown': (event: Event) => {
          handleFroalaTableMouseDown(this.editor, event);
        },

        // Toolbar commands (bold, image insert, lists, etc.) are genuine edits
        // that may not always fire a native 'input' event.
        'commands.after': (command: string, alignValue: string) => {
          if (command !== 'getPDF' && !this.isPageSwitching && !this.preventAutoPaste) {
            this.hasUserEditedSinceLoad = true;
          }

          if (command === 'fullscreen') {
            setTimeout(() => this.syncFullscreenState(), 0);
          }

          if (command === 'imageAlign') {
            const alignedImageElement = this.pendingImageAlignElement || this.getCurrentImageElement();
            this.normalizeFroalaImageAlignment(alignedImageElement, alignValue);
            this.refreshFroalaImageOverlay(alignedImageElement);
            this.pendingImageAlignElement = null;
            this.relabelImageAlignCenterOption();
          }
        },
        
        'keydown': (e: KeyboardEvent) => {
          const listBackspaceResult = handleFroalaListBackspace(this.editor, e);

          if (listBackspaceResult.handled) {
            if (listBackspaceResult.changed) {
              this.hasUserEditedSinceLoad = true;
              setTimeout(() => {
                this.handleContentChange(this.editor.html.get());
              }, 0);
            }

            return false;
          }

          const isBackspaceOrDelete = e.key === 'Backspace' || e.key === 'Delete' || e.keyCode === 8 || e.keyCode === 46;
          if (isBackspaceOrDelete && !this.isPageSwitching && !this.preventAutoPaste && this.editor?.html?.get) {
            // Treat delete keys as genuine user edits so partial removals on
            // existing pages can still become dirty even when Froala skips
            // native input notifications.
            this.hasUserEditedSinceLoad = true;
            const previousContent = this.editor.html.get();

            const syncDeletionChange = () => {
              if (this.isPageSwitching || this.preventAutoPaste || !this.editor?.html?.get) {
                return false;
              }

              const nextContent = this.editor.html.get();
              if (nextContent !== previousContent) {
                this.lastKnownContent = nextContent;
                this.handleContentChange(nextContent);
                return true;
              }

              return false;
            };

            // Froala may commit DOM edits slightly after keydown; check now and
            // once more on the next frame so delete/backspace changes are picked up.
            setTimeout(() => {
              if (syncDeletionChange()) {
                return;
              }

              requestAnimationFrame(() => {
                syncDeletionChange();
              });
            }, 0);
          }

          // Allow deletion and navigation keys even when content exceeds A4 size
          const allowedKeys = [
            8,   // Backspace
            46,  // Delete
            37,  // Left arrow
            38,  // Up arrow
            39,  // Right arrow
            40,  // Down arrow
            35,  // End
            36,  // Home
            27,  // Escape
            9,   // Tab
            16,  // Shift
            17,  // Ctrl
            18,  // Alt
            91,  // Cmd (Mac)
            93   // Cmd (Mac)
          ];
          
          // Copy, select-all, and cut remain available while the legacy page is locked.
          const isCtrlOrCmd = e.ctrlKey || e.metaKey;
          const isAllowedShortcut = isCtrlOrCmd && ['a', 'c', 'x'].includes(e.key.toLowerCase());
          
          // Check if typing is disabled due to A4 size limit
          if (!this.allowTyping) {
            if (!allowedKeys.includes(e.keyCode) && !isAllowedShortcut) {
              console.log('Blocking keypress - content exceeds A4 size limit. Key:', e.keyCode);
              e.preventDefault();
              e.stopPropagation();
              
              // Show message periodically (not on every keypress)
              if (!this.lastA4WarningTime || Date.now() - this.lastA4WarningTime > 2000) {
                this.toastService.presentToast(
                  'Content exceeds A4 size. Please delete some content to continue typing.', 
                  2000, 
                  AppConstants.WARNING_SNACKBAR, 
                  'top'
                );
                this.lastA4WarningTime = Date.now();
              }
              
              return false;
            }
          }
          
          return true;
        }
      }
    };
  }

  /**
   * Handle editor initialization from Froala directive
   */
  private onEditorInitialized(editor: any): void {
    try {
      console.log('Setting up editor instance and verifying methods...', editor.html);
      this.editor = editor;
      
      // Wait for editor methods to be available
      this.waitForEditorMethods();
      
    } catch (error) {
      console.error('Error during editor initialization:', error);
      this.editorInitialized = false;
    }
  }

  /**
   * Verify editor methods are available (should be called after initialized event)
   */
  private waitForEditorMethods(): void {
    console.log('Verifying editor methods...');
    
    // Check immediately first
    if (this.isEditorReady()) {
      console.log('Editor ready immediately');
      this.completeEditorInitialization();
      return;
    }
    
    console.log('Editor not ready, attempting retries...');
    
    // First retry after 200ms
    setTimeout(() => {
      if (this.isEditorReady()) {
        console.log('Editor ready on first retry (200ms)');
        this.completeEditorInitialization();
        return;
      }
      
      // Second retry after 500ms
      setTimeout(() => {
        if (this.isEditorReady()) {
          console.log('Editor ready on second retry (500ms)');
          this.completeEditorInitialization();
          return;
        }
        
        // Final retry after 1000ms
        setTimeout(() => {
          if (this.isEditorReady()) {
            console.log('Editor ready on final retry (1000ms)');
            this.completeEditorInitialization();
          } else {
            console.error('Editor initialization failed - methods not available after all retries');
            console.log('Final editor state:', {
              hasEditor: !!this.editor,
              hasHtml: !!(this.editor && this.editor.html),
              hasGet: !!(this.editor && this.editor.html && typeof this.editor.html.get === 'function'),
              hasSet: !!(this.editor && this.editor.html && typeof this.editor.html.set === 'function'),
              editorType: typeof this.editor,
              htmlType: typeof this.editor?.html
            });
            
            // Force set to false to prevent further attempts
            this.editorInitialized = false;
          }
        }, 1000);
      }, 500);
    }, 200);
  }
  
  /**
   * Check if editor is ready with all required methods
   */
  private isEditorReady(): boolean {
    const ready = !!(this.editor && 
              this.editor.html && 
              typeof this.editor.html.get === 'function' && 
              typeof this.editor.html.set === 'function');
    
    // Add debugging for the ready check
    if (!ready) {
      console.log('isEditorReady() check failed:', {
        hasEditor: !!this.editor,
        hasHtml: !!(this.editor && this.editor.html),
        hasGet: !!(this.editor && this.editor.html && typeof this.editor.html.get === 'function'),
        hasSet: !!(this.editor && this.editor.html && typeof this.editor.html.set === 'function'),
        editorType: typeof this.editor,
        editorReference: this.editor
      });
    }
    
    return ready;
  }
  
  /**
   * Complete the editor initialization process
   */
  private completeEditorInitialization(): void {
    console.log('Completing editor initialization...');
    this.editorInitialized = true;
    this.syncFullscreenState();

    // Track genuine user edits so focus-only DOM normalization is not treated
    // as a change (see hasUserEditedSinceLoad).
    this.attachUserEditListener();

    // Override print.toPDF on the editor instance so the getPDF toolbar button
    // uses our custom logic (filename prompt + base64 image conversion).
    if (this.editor && this.editor.print) {
      this.editor.print.toPDF = () => {
        this.exportEditorToPDF();
      };
      console.log('print.toPDF overridden with custom PDF export');
    }
    
    // Perform initial setup with small delays to ensure stability
    setTimeout(() => {
      try {
        this.setDefaultFont();
      } catch (error) {
        console.warn('Error setting default font:', error);
      }
    }, 50);
    
    setTimeout(() => {
      try {
        this.loadCurrentPageContent();
      } catch (error) {
        console.warn('Error loading current page content:', error);
      }
    }, 100);
    
    // Check for any pasted images that might be waiting for upload
    setTimeout(() => {
      try {
        this.checkAndHandlePastedImages();
      } catch (error) {
        console.warn('Error checking pasted images:', error);
      }
    }, 200);
    
    console.log('Editor fully initialized and ready for use');
  }

  /**
   * Try alternative initialization approach
   */
  /**
   * Load content for the current active page
   */
  private loadCurrentPageContent(): void {
    console.log('loadCurrentPageContent called - checking editor status:', {
      editorInitialized: this.editorInitialized,
      hasEditor: !!this.editor,
      hasHtml: !!(this.editor && this.editor.html),
      hasHtmlSet: !!(this.editor && this.editor.html && typeof this.editor.html.set === 'function'),
      pagesLength: this.pages.length,
      activePage: this.activePage,
      isEditorReady: this.isEditorReady()
    });

    if (!this.editorInitialized || !this.editor || this.pages.length === 0) {
      console.debug('Cannot load page content - editor not ready or no pages available');
      return; // Don't retry here, let the initialization process handle it
    }

    // Additional check for html methods availability
    if (!this.isEditorReady()) {
      console.debug('Editor html methods not available yet');
      return; // Don't retry here, let the initialization process handle it
    }

    try {
      const currentPage = this.pages.find(page => page.id === this.activePage);
      let content = currentPage?.content || '';
      
      console.log('Loading content for page:', this.activePage, 'Content length:', content.length);
      
      // Set content directly since editor is verified to be ready
      this.editor.html.set(content);
      normalizeFroalaTableVerticalAlignCells(this.editor);
      this.highlightPlaceholders();

      const loadedEditorContent = this.editor.html.get();

      // Capture the normalized baseline (what the editor actually holds) so that
      // dirtiness is measured against this rather than the raw stored HTML.
      this.activePageBaselineContent = this.normalizeHtmlForComparison(loadedEditorContent);
      // Fresh page load: no genuine user edit has happened yet.
      this.hasUserEditedSinceLoad = false;

      this.contentHeight = this.getMeasuredReportContentHeight(loadedEditorContent);
      this.allowTyping = this.contentHeight <= this.getReportSafePageHeight();
      this.loadedContentExceedsA4 = !this.allowTyping;

      if (this.loadedContentExceedsA4) {
        console.warn('Loaded content exceeds A4 size. Height:', this.contentHeight, 'Max:', this.getReportSafePageHeight());
        this.setA4StatusMessage(
          'This saved content exceeds A4 page size. Existing content was preserved; delete content or choose to remove the excess.',
          'warning',
          false
        );
        this.promptForLegacyA4Overflow(currentPage, loadedEditorContent);
      } else {
        this.clearA4StatusMessage();
      }

      this.lastKnownContent = loadedEditorContent;
      this.lastValidA4Content = this.allowTyping ? loadedEditorContent : '';
      this.initialPageContentLoaded = true;
      this.scheduleLoadedPageA4Validation(currentPage, loadedEditorContent);
      
      console.log('Content loaded successfully - page switching unblocked');
    } catch (error) {
      console.error('Error loading page content:', error);
      
      // Don't try retries here - if there's an error, it's likely a deeper issue
      // Let the user know there's a problem
      console.error('Failed to load page content - editor may be in an invalid state');
    }
  }

  private scheduleLoadedPageA4Validation(page: Page | undefined, loadedContent: string): void {
    if (!page) {
      return;
    }

    const pageId = page.id;
    const loadedContentSnapshot = this.normalizeHtmlForComparison(loadedContent);
    const validateLoadedPage = (): void => {
      if (!this.editor?.html?.get || this.activePage !== pageId) {
        return;
      }

      const currentContent = this.editor.html.get();
      if (this.normalizeHtmlForComparison(currentContent) !== loadedContentSnapshot) {
        return;
      }

      this.contentHeight = this.getMeasuredReportContentHeight(currentContent);
      this.allowTyping = this.contentHeight <= this.getReportSafePageHeight();
      this.loadedContentExceedsA4 = !this.allowTyping;

      if (this.loadedContentExceedsA4) {
        this.setA4StatusMessage(
          'This saved content exceeds A4 page size. Existing content was preserved; delete content or choose to remove the excess.',
          'warning',
          false
        );
        this.promptForLegacyA4Overflow(page, currentContent);
      } else {
        this.lastValidA4Content = currentContent;
        if (this.a4StatusMessageType === 'warning') {
          this.clearA4StatusMessage();
        }
      }
    };
    const scheduleAfterLayout = (): void => {
      requestAnimationFrame(() => requestAnimationFrame(validateLoadedPage));
    };

    // Recheck after Froala's first layout so initial page validation does not
    // depend on the user switching away and back to trigger another measure.
    scheduleAfterLayout();

    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleAfterLayout);
    }

    const images = Array.from(this.editor?.el?.querySelectorAll?.('img') || []) as HTMLImageElement[];
    images.forEach(image => {
      if (!image.complete) {
        image.addEventListener('load', scheduleAfterLayout, { once: true });
        image.addEventListener('error', scheduleAfterLayout, { once: true });
      }
    });
  }

  /**
   * Handle content changes in the editor
   */
  private handleContentChange(content: string): void {
    if (this.isFileNameAndLanguageInvalid()) {
      this.toastService.presentToast(
        this.translate.instant('File name and language cannot be empty'), 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
      return;
    }

    // Check for data URI images before saving
    if (content.includes('src="data:')) {
      console.warn('⚠️ Content contains data URI images - these should be uploaded first');
      console.log('Checking for data URI images in content...');
      
      // Extract data URI images for debugging
      const dataUriMatches = content.match(/src="data:[^"]+"/g);
      if (dataUriMatches) {
        console.log('Found data URI images:', dataUriMatches.length);
        dataUriMatches.forEach((match, index) => {
          console.log(`Data URI ${index + 1}:`, match.substring(0, 100) + '...');
        });
        
        // Check if editor is fully initialized before handling images
        if (!this.editorInitialized || !this.editor || !this.editor.html) {
          console.warn('⚠️ Editor not fully initialized - retrying content change in 1 second');
          setTimeout(() => {
            if (this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
              const retryContent = this.editor.html.get();
              this.handleContentChange(retryContent);
            }
          }, 1000);
          return;
        }

        // If uploads are already in progress, just wait without interfering
        if (this.imageUploadInProgress || this.pastedImageUploadInProgress) {
          console.log('🔄 Image upload already in progress - letting it complete naturally...');
          // Don't intervene, let the upload process complete and trigger normal content change
          return;
        }
        
        // Only intervene if we have data URIs but no upload process is running
        const dataUriImages = this.editor.el?.querySelectorAll('img[src^="data:"]');
        if (dataUriImages && dataUriImages.length > 0) {
          console.log(`Found ${dataUriImages.length} data URI images in DOM without active upload process`);
          
          // Give imagePasteProcess a chance to work first (short delay)
          console.log('Giving imagePasteProcess a brief moment to start...');
          setTimeout(() => {
            // Check if upload process started during our wait
            if (this.imageUploadInProgress || this.pastedImageUploadInProgress) {
              console.log('✅ Upload process started during wait - letting it complete');
              return;
            }
            
            // Check if images were processed during our wait
            const remainingDataImages = this.editor.el?.querySelectorAll('img[src^="data:"]');
            if (!remainingDataImages || remainingDataImages.length === 0) {
              console.log('✅ Images were processed during wait - proceeding with save');
              this.processContentChange(content);
              return;
            }
            
            // If we still have data URIs and no upload process, try manual upload as fallback
            console.log('⚠️ Images still present after waiting - triggering manual upload as fallback');
            try {
              this.pastedImageUploadInProgress = true;
              this.imageUploadInProgress = true;
              this.triggerImageUploadForPastedImages(remainingDataImages);
              
              // Wait for manual upload to complete
              this.waitForImageUploadsAndSave(content, 0);
            } catch (error) {
              console.error('❌ Failed to trigger manual upload:', error);
              // Reset flags and proceed with save anyway
              this.pastedImageUploadInProgress = false;
              this.imageUploadInProgress = false;
              this.processContentChange(content);
            }
          }, 800); // Brief delay to let automatic process work
          
          return;
        } else {
          console.log('No data URI images found in DOM - content may have been updated');
        }
        
        // Fallback for edge cases - but with shorter delay
        console.log('Applying fallback delay for edge cases...');
        setTimeout(() => {
          // Re-check content after delay
          if (this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
            const latestContent = this.editor.html.get();
            if (latestContent.includes('src="data:')) {
              console.warn('⚠️ Data URI images still present after fallback delay');
            } else {
              console.log('✅ Data URI images resolved during fallback delay');
            }
            this.processContentChange(latestContent);
          }
        }, 1200); // Reduced from 2000ms to 1200ms
        return;
      }
    }

    this.processContentChange(content);
  }

  /**
   * Wait for image uploads to complete and then save content
   */
  private waitForImageUploadsAndSave(content: string, retryCount: number): void {
    const maxRetries = 6; // Reduced from 10 to 6 (3 seconds total instead of 5)
    const retryDelay = 500; // Check every 500ms
    
    if (retryCount >= maxRetries) {
      console.warn('⚠️ Upload monitoring timeout reached - proceeding with save');
      console.log('Upload flags at timeout:', {
        imageUploadInProgress: this.imageUploadInProgress,
        pastedImageUploadInProgress: this.pastedImageUploadInProgress,
        queueSize: this.imageUploadQueue?.size || 0
      });
      
      // Reset flags to prevent permanent blocking
      this.pastedImageUploadInProgress = false;
      this.imageUploadInProgress = false;
      this.processContentChange(content);
      return;
    }

    // Check if uploads are still in progress
    if (this.imageUploadInProgress || this.pastedImageUploadInProgress || 
        (this.imageUploadQueue && this.imageUploadQueue.size > 0)) {
      console.log(`🔄 Waiting for image uploads... (retry ${retryCount + 1}/${maxRetries})`);
      
      // Also check if the actual images in DOM are still data URIs
      if (this.editor && this.editor.el) {
        const remainingDataImages = this.editor.el.querySelectorAll('img[src^="data:"]');
        if (remainingDataImages.length === 0) {
          console.log('✅ No data URI images remaining in DOM - uploads completed successfully');
          // Clear flags and proceed
          this.pastedImageUploadInProgress = false;
          this.imageUploadInProgress = false;
          if (this.imageUploadQueue) {
            this.imageUploadQueue.clear();
          }
          
          // Get latest content and save
          const latestContent = this.editor.html.get();
          this.processContentChange(latestContent);
          return;
        }
      }
      
      setTimeout(() => {
        this.waitForImageUploadsAndSave(content, retryCount + 1);
      }, retryDelay);
      return;
    }

    // Uploads completed - get latest content and save
    console.log('✅ Image uploads completed - proceeding with save');
    if (this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
      const latestContent = this.editor.html.get();
      this.processContentChange(latestContent);
    } else {
      // Fallback to provided content
      this.processContentChange(content);
    }
  }

  /**
   * Process content change (extracted from handleContentChange)
   */
  private processContentChange(content: string): void {
    this.checkContentSize(content);
  }

  /**
   * Insert the largest prefix of complete clipboard blocks that fits the page.
   * Returns false when the clipboard or selection cannot be represented safely,
   * allowing Froala's normal paste handling to take over.
   */
  private insertLargestFittingPaste(event: ClipboardEvent): boolean {
    const clipboardData = event?.clipboardData;
    const editorElement = this.editor?.el as HTMLElement;
    const selection = window.getSelection();

    if (!clipboardData || !editorElement || !selection || selection.rangeCount === 0 ||
        !this.editor?.selection?.save || !this.editor?.selection?.restore || !this.editor?.html?.insert) {
      return false;
    }

    const sourceRange = selection.getRangeAt(0);
    if (!editorElement.contains(sourceRange.commonAncestorContainer)) {
      return false;
    }

    const pasteUnits = this.getClipboardPasteUnits(clipboardData);
    if (pasteUnits.length === 0) {
      return false;
    }

    this.preventAutoPaste = true;
    const isEmptyDestination = this.isEditorContentEmpty(this.lastKnownContent);
    let contentWithSelectionMarkers = '';

    if (!isEmptyDestination) {
      this.editor.selection.save();
      contentWithSelectionMarkers = this.editor.html.get(true);
    }

    const buildCandidate = (unitCount: number): string | null => {
      try {
        if (isEmptyDestination) {
          this.editor.html.set(pasteUnits.slice(0, unitCount).join(''));
          return this.editor.html.get();
        }

        this.editor.html.set(contentWithSelectionMarkers);
        this.editor.selection.restore();
        this.editor.html.insert(pasteUnits.slice(0, unitCount).join(''));
        return this.editor.html.get();
      } catch (error) {
        console.warn('Unable to test pasted content with Froala:', error);
        return null;
      }
    };

    let low = 1;
    let high = pasteUnits.length;
    let fittingUnitCount = 0;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const candidate = buildCandidate(middle);
      if (candidate !== null && !this.wouldContentExceedA4(candidate)) {
        fittingUnitCount = middle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    if (fittingUnitCount > 0) {
      const insertedContent = buildCandidate(fittingUnitCount);
      if (insertedContent === null) {
        this.editor.html.set(this.lastKnownContent);
        this.moveCaretToEndOfEditor();
        this.preventAutoPaste = false;
        return true;
      }

      this.hasUserEditedSinceLoad = true;
      this.lastKnownContent = insertedContent;
      this.checkContentSize(insertedContent);
      if (isEmptyDestination) {
        this.moveCaretToEndOfEditor();
      }
    } else {
      this.editor.html.set(this.lastKnownContent);
      this.moveCaretToEndOfEditor();
    }

    setTimeout(() => {
      this.preventAutoPaste = false;
    }, 0);

    if (fittingUnitCount < pasteUnits.length && Date.now() - this.lastA4WarningTime > 2000) {
      this.showA4TruncatedMessage();
      this.lastA4WarningTime = Date.now();
    }

    return true;
  }

  private getClipboardPasteUnits(clipboardData: DataTransfer): string[] {
    const html = this.stripClipboardMetadataFromHtml(clipboardData.getData('text/html'));
    const plainText = this.stripClipboardMetadataFromText(clipboardData.getData('text/plain'));
    const plainTextLines = plainText ? plainText.split('\n') : [];

    if (html) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      let clipboardNodes = Array.from(parsedDocument.body.childNodes);
      const singleElement = clipboardNodes.length === 1 && clipboardNodes[0] instanceof HTMLElement
        ? clipboardNodes[0] as HTMLElement
        : null;

      if (singleElement?.matches('div, section, article') && singleElement.childNodes.length > 1) {
        clipboardNodes = Array.from(singleElement.childNodes);
      }

      const units = clipboardNodes
        .filter(node => node.nodeType !== Node.ELEMENT_NODE || !['META', 'STYLE', 'SCRIPT'].includes((node as Element).tagName))
        .map(node => (node as HTMLElement).outerHTML || node.textContent || '')
        .filter(unit => unit.length > 0);
      if (units.length > 1 || (units.length === 1 && plainTextLines.length <= 1)) {
        return units;
      }
    }

    if (plainTextLines.length > 1) {
      return plainTextLines.map(line => {
        const lineElement = document.createElement('div');
        lineElement.textContent = line;
        return `<p>${lineElement.innerHTML || '<br>'}</p>`;
      });
    }

    if (plainText) {
      const textElement = document.createElement('span');
      textElement.textContent = plainText;
      return [textElement.outerHTML];
    }

    return [];
  }

  private stripClipboardMetadataFromHtml(html: string): string {
    if (!html) {
      return '';
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    container.querySelectorAll('meta, style, script, link').forEach((element) => element.remove());

    const commentNodes: Comment[] = [];
    const commentWalker = document.createTreeWalker(container, NodeFilter.SHOW_COMMENT);
    let commentNode = commentWalker.nextNode();
    while (commentNode) {
      commentNodes.push(commentNode as Comment);
      commentNode = commentWalker.nextNode();
    }

    commentNodes.forEach((commentNode) => {
      if (this.isClipboardMetadataLine(commentNode.data)) {
        commentNode.parentNode?.removeChild(commentNode);
      }
    });

    const textNodes: Text[] = [];
    const textWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let textNode = textWalker.nextNode();
    while (textNode) {
      textNodes.push(textNode as Text);
      textNode = textWalker.nextNode();
    }

    textNodes.forEach((textNode) => {
      const sanitizedText = this.stripClipboardMetadataFromText(textNode.textContent || '');
      if (!sanitizedText) {
        textNode.parentNode?.removeChild(textNode);
        return;
      }

      if (sanitizedText !== textNode.textContent) {
        textNode.textContent = sanitizedText;
      }
    });

    return container.innerHTML;
  }

  private normalizePastedBlockStructure(html: string): string {
    const sanitizedHtml = this.stripClipboardMetadataFromHtml(html);

    if (!sanitizedHtml) {
      return sanitizedHtml;
    }

    if (!/(<br\s*\/?>|<\/(?:div|p|h[1-6])>\s*<(?:div|p|h[1-6])\b)/i.test(sanitizedHtml)) {
      return sanitizedHtml;
    }

    const container = document.createElement('div');
    container.innerHTML = sanitizedHtml;

    if (container.querySelector('ul, ol, li, table, tr, td, th, img, video, iframe, blockquote, pre')) {
      return sanitizedHtml;
    }

    const topLevelElements = Array.from(container.children);
    const shouldNormalize = topLevelElements.length <= 1 || topLevelElements.every((element) => {
      const tagName = element.tagName;
      return ['DIV', 'P', 'SPAN', 'STRONG', 'EM', 'B', 'I', 'U', 'A'].includes(tagName) || !!element.querySelector('br');
    });

    if (!shouldNormalize) {
      return sanitizedHtml;
    }

    const lines = this.extractPastedHtmlLines(sanitizedHtml);
    if (lines.length < 2) {
      return sanitizedHtml;
    }

    const normalizedHtml = this.buildSemanticBlocksFromPasteLines(lines);
    return normalizedHtml || sanitizedHtml;
  }

  private extractPastedHtmlLines(html: string): string[] {
    const container = document.createElement('div');
    container.innerHTML = html;

    const topLevelElements = Array.from(container.children) as HTMLElement[];
    if (topLevelElements.length > 1 && topLevelElements.every((element) => this.isSimplePastedBlockElement(element))) {
      return topLevelElements
        .map((element) => this.normalizePastedLineFragment(element.outerHTML))
        .filter((line) => this.fragmentHasRenderableContent(line));
    }

    const firstElement = topLevelElements[0] as HTMLElement | undefined;
    if (topLevelElements.length === 1 && firstElement && this.isSimplePastedBlockElement(firstElement)) {
      const childBlockElements = Array.from(firstElement.children) as HTMLElement[];

      if (childBlockElements.length > 1 && childBlockElements.every((element) => this.isSimplePastedBlockElement(element))) {
        return childBlockElements
          .map((element) => this.normalizePastedLineFragment(element.outerHTML))
          .filter((line) => this.fragmentHasRenderableContent(line));
      }

      return this.extractPastedLinesFromSingleContainer(firstElement);
    }

    return this.extractPastedLinesFromSingleContainer(container);
  }

  private extractPastedLinesFromSingleContainer(container: HTMLElement): string[] {
    const lines: string[] = [];
    let lineContainer = document.createElement('div');

    Array.from(container.childNodes).forEach((node) => {
      if (node instanceof HTMLBRElement) {
        const lineHtml = this.normalizePastedLineFragment(lineContainer.innerHTML);
        if (this.fragmentHasRenderableContent(lineHtml)) {
          lines.push(lineHtml);
        }
        lineContainer = document.createElement('div');
        return;
      }

      lineContainer.appendChild(node.cloneNode(true));
    });

    const trailingLineHtml = this.normalizePastedLineFragment(lineContainer.innerHTML);
    if (this.fragmentHasRenderableContent(trailingLineHtml)) {
      lines.push(trailingLineHtml);
    }

    return lines;
  }

  private isSimplePastedBlockElement(element: HTMLElement): boolean {
    if (!['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(element.tagName)) {
      return false;
    }

    return !element.querySelector('ul, ol, li, table, tr, td, th, img, video, iframe, blockquote, pre');
  }

  private normalizePastedLineFragment(fragment: string): string {
    if (!fragment) {
      return '';
    }

    const container = document.createElement('div');
    container.innerHTML = fragment.trim();

    while (container.childElementCount === 1) {
      const onlyChild = container.firstElementChild as HTMLElement | null;

      if (!onlyChild || !['DIV', 'P', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI'].includes(onlyChild.tagName)) {
        break;
      }

      if (onlyChild.attributes.length > 0 && !['SPAN'].includes(onlyChild.tagName)) {
        break;
      }

      container.innerHTML = onlyChild.innerHTML.trim();
    }

    return container.innerHTML.trim();
  }

  private buildSemanticBlocksFromPasteLines(lines: string[]): string {
    const blocks: string[] = [];
    let activeListType: 'ul' | 'ol' | null = null;
    let activeListItems: string[] = [];

    const flushList = () => {
      if (!activeListType || activeListItems.length === 0) {
        activeListType = null;
        activeListItems = [];
        return;
      }

      blocks.push(`<${activeListType}>${activeListItems.join('')}</${activeListType}>`);
      activeListType = null;
      activeListItems = [];
    };

    lines.forEach((lineHtml) => {
      const lineText = this.getFragmentTextContent(lineHtml);
      const listType = this.getPastedListType(lineText);

      if (listType) {
        if (activeListType && activeListType !== listType) {
          flushList();
        }

        activeListType = listType;
        activeListItems.push(`<li>${this.stripLeadingListMarkerFromHtml(lineHtml, listType)}</li>`);
        return;
      }

      flushList();
      blocks.push(`<p>${lineHtml}</p>`);
    });

    flushList();
    return blocks.join('');
  }

  private getPastedListType(text: string): 'ul' | 'ol' | null {
    if (/^\s*(?:[\u2022\u25CF\u25E6\u00B7\-\*])\s+/.test(text)) {
      return 'ul';
    }

    if (/^\s*\d+[.)]\s+/.test(text)) {
      return 'ol';
    }

    return null;
  }

  private stripLeadingListMarkerFromHtml(lineHtml: string, listType: 'ul' | 'ol'): string {
    const container = document.createElement('div');
    container.innerHTML = lineHtml;
    const markerPattern = listType === 'ol'
      ? /^\s*\d+[.)]\s*/
      : /^\s*(?:[\u2022\u25CF\u25E6\u00B7\-\*])\s*/;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();

    while (currentNode) {
      const textNode = currentNode as Text;
      const textValue = textNode.textContent || '';

      if (textValue.trim().length > 0) {
        textNode.textContent = textValue.replace(markerPattern, '');
        break;
      }

      currentNode = walker.nextNode();
    }

    return container.innerHTML.trim();
  }

  private fragmentHasRenderableContent(fragment: string): boolean {
    if (!fragment) {
      return false;
    }

    const container = document.createElement('div');
    container.innerHTML = fragment;

    if (container.querySelector('img, svg, table, hr, iframe, video')) {
      return true;
    }

    return (container.textContent || '').replace(/\u00a0/g, ' ').trim().length > 0;
  }

  private getFragmentTextContent(fragment: string): string {
    const container = document.createElement('div');
    container.innerHTML = fragment;
    return (container.textContent || '').replace(/\u00a0/g, ' ').trim();
  }

  private stripClipboardMetadataFromText(text: string): string {
    if (!text) {
      return '';
    }

    const normalizedText = text.replace(/\r\n?/g, '\n').replace(/\uFEFF/g, '');
    const sanitizedLines = normalizedText
      .split('\n')
      .filter(line => {
        const normalizedLine = line.replace(/\u00a0/g, ' ').trim();
        return normalizedLine.length > 0 && !this.isClipboardMetadataLine(normalizedLine);
      });

    return sanitizedLines.join('\n');
  }

  private isClipboardMetadataLine(value: string): boolean {
    const normalizedValue = (value || '')
      .replace(/^<!--/, '')
      .replace(/-->$/, '')
      .replace(/\s+/g, ' ')
      .trim();

    return /^(startfragment|endfragment|starthtml|endhtml|sourceurl)(?::.*)?$/i.test(normalizedValue);
  }

  /**
   * Update content for the current page
   */
  private updatePageContent(content: string): void {
    const pageIndex = this.pages.findIndex(page => page.id === this.activePage);
    if (pageIndex !== -1) {
      const page = this.pages[pageIndex];
      const persistedContent = this.stripTransientEditorSelectionClasses(content);
      page.content = persistedContent;
      const isChanged = this.computePageDirty(page, persistedContent);
      page.isDirty = isChanged;
      this.isUserTyping = isChanged;
    }
  }

  private stripTransientEditorSelectionClasses(content: string): string {
    if (!content) {
      return content;
    }

    if (!/(blueprint-table-selected-cell|blueprint-selected-neighbor-|fr-selected-cell|fr-cell-fixed|fr-cell-handler)/.test(content)) {
      return content;
    }

    const container = document.createElement('div');
    container.innerHTML = content;
    this.removeTransientEditorSelectionClasses(container);
    return container.innerHTML;
  }

    /**
   * Decide whether the given page is dirty for the given editor content.
   *
   * A page is dirty when either:
   *  - the user genuinely edited it AND the normalized content differs from the
   *    loaded/saved baseline (ignores focus-only re-normalization), or
   *  - the user cleared ALL content from an EXISTING page that previously held
   *    saved data. This transition (saved content -> empty) must be persisted so
   *    the blank state is saved and the old data does not reappear later. Brand
   *    new/blank pages are excluded because their savedContent is empty, so we
   *    never prompt "save the blank page" for a page that never had data.
   */
  private computePageDirty(page: Page, content: string): boolean {
    const editedAndDiffers = this.hasUserEditedSinceLoad &&
      this.normalizeHtmlForComparison(content) !== this.activePageBaselineContent;

    const clearedExistingPage = !this.isPageSwitching &&
      this.isEditorContentEmpty(content) &&
      this.pagePreviouslyHadSavedContent(page);

    return this.isNameDirty || editedAndDiffers || clearedExistingPage;
  }

  /**
   * True when the editor HTML holds no meaningful content. Treats Froala's empty
   * variants (``, `<p><br></p>`, `<p>&nbsp;</p>`, etc.) as empty, while media/
   * structural elements (images, tables, lists, rules) count as real content.
   */
  private isEditorContentEmpty(html: string): boolean {
    if (!html) {
      return true;
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    if (container.querySelector('img, table, hr, iframe, video, ul, ol')) {
      return false;
    }
    const text = (container.textContent || '').replace(/\u00a0/g, ' ').trim();
    return text.length === 0;
  }

  /**
   * True when the page previously had saved (non-empty) content persisted to the
   * backend. Used to distinguish an existing page that the user just emptied from
   * a brand new blank page.
   */
  private pagePreviouslyHadSavedContent(page: Page | undefined): boolean {
    return !!page && !this.isEditorContentEmpty(page.savedContent || '');
  }

  /**
   * Normalize editor HTML for dirtiness comparison. Froala lazily re-serializes
   * the DOM on the first focus/interaction, producing a string that differs from
   * the value returned immediately after `html.set()` even though nothing was
   * edited. Routing both sides through a detached DOM element (and collapsing
   * whitespace) makes those cosmetic re-serialization differences equal, so only
   * real content changes are detected as dirty.
   */
  private normalizeHtmlForComparison(html: string): string {
    if (!html) {
      return '';
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    this.removeTransientEditorSelectionClasses(container);
    return container.innerHTML.replace(/\s+/g, ' ').trim();
  }

  /**
   * Attach a native `input` listener on the editor element. The native input
   * event fires only for real user edits (typing, delete, cut, paste, drop) and
   * NOT for Froala's programmatic re-normalization on first focus, giving us a
   * reliable signal for genuine changes.
   */
  private attachUserEditListener(): void {
    if (this.userEditListenerAttached || !this.editor || !this.editor.el) {
      return;
    }
    this.editor.el.addEventListener('input', () => {
      this.hasUserEditedSinceLoad = true;
    });
    this.userEditListenerAttached = true;
  }

  /**
   * Check if content exceeds A4 size and enforce restrictions
   */
  private checkContentSize(content: string): void {
    if (!this.editor) return;

    this.contentHeight = this.getMeasuredReportContentHeight(content);

    if (this.contentHeight <= this.getReportSafePageHeight()) {
      this.allowTyping = true;
      this.loadedContentExceedsA4 = false;
      this.lastValidA4Content = content;
      if (this.a4StatusMessageType === 'warning') {
        this.clearA4StatusMessage();
      }
      this.updatePageContent(content);
      return;
    }

    // Froala re-normalizes the DOM on first focus, which can momentarily measure
    // a hair over the limit even though no real content was added. Accept it (no
    // rollback, no caret jump) when the content matches the last valid state.
    if (this.lastValidA4Content &&
        this.normalizeHtmlForComparison(content) === this.normalizeHtmlForComparison(this.lastValidA4Content)) {
      this.allowTyping = true;
      this.updatePageContent(content);
      return;
    }

    if (this.loadedContentExceedsA4) {
      this.allowTyping = false;
      this.lastKnownContent = content;
      this.setA4StatusMessage(
        'This saved content exceeds A4 page size. Existing content was preserved; delete content or choose to remove the excess.',
        'warning',
        false
      );
      this.updatePageContent(content);
      return;
    }

    if (this.editor.html && typeof this.editor.html.set === 'function' && this.lastValidA4Content) {
      this.preventAutoPaste = true;
      this.editor.html.set(this.lastValidA4Content);
      this.updatePageContent(this.lastValidA4Content);
      this.lastKnownContent = this.lastValidA4Content;
      this.moveCaretToEndOfEditor();
      setTimeout(() => {
        this.preventAutoPaste = false;
      }, 0);
    }

    // Reject only a new overflowing edit; previously valid content remains usable.
    this.allowTyping = true;

    if (Date.now() - this.lastA4WarningTime > 2000) {
      this.showA4TruncatedMessage();
      this.lastA4WarningTime = Date.now();
    }
  }

  private setA4StatusMessage(message: string, type: 'info' | 'warning', autoClear: boolean = true): void {
    this.a4StatusMessage = message;
    this.a4StatusMessageType = type;

    if (this.a4StatusMessageTimer) {
      clearTimeout(this.a4StatusMessageTimer);
      this.a4StatusMessageTimer = null;
    }

    if (autoClear) {
      this.a4StatusMessageTimer = setTimeout(() => {
        if (this.a4StatusMessage === message) {
          this.a4StatusMessage = '';
        }
        this.a4StatusMessageTimer = null;
      }, 5000);
    }
  }

  private clearA4StatusMessage(): void {
    if (this.a4StatusMessageTimer) {
      clearTimeout(this.a4StatusMessageTimer);
      this.a4StatusMessageTimer = null;
    }

    this.a4StatusMessage = '';
  }

  private showA4TruncatedMessage(): void {
    const message = 'Content was truncated to fit within A4 page size.';
    this.setA4StatusMessage(message, 'info');
    this.toastService.presentToast(
      message,
      3000,
      AppConstants.WARNING_SNACKBAR,
      'top'
    );
  }

  private promptForLegacyA4Overflow(page: Page | undefined, content: string): void {
    if (!page || !this.pagePreviouslyHadSavedContent(page)) {
      return;
    }

    const pageKey = page.pagesId || `${this.templateId || 'template'}:${page.id}`;
    if (this.promptedLegacyA4OverflowPageKeys.has(pageKey)) {
      return;
    }

    this.promptedLegacyA4OverflowPageKeys.add(pageKey);

    setTimeout(async () => {
      const activePage = this.pages.find(candidate => candidate.id === this.activePage);
      if (!activePage || activePage.id !== page.id || !this.loadedContentExceedsA4) {
        return;
      }

      const shouldTruncate = await this.confirmationService.presentConfirmationModal(
        this.translate.instant('exceed_a4_size'),
        'Remove Excess',
        false,
        'Keep Content',
        undefined,
        {
          confirmationMessage: this.translate.instant('excess_content_message'),
          showActionButton: false,
          useFullMessage: true
        },
      );

      if (shouldTruncate && activePage.id === this.activePage) {
        this.truncateCurrentPageToA4(content);
      }
    }, 0);
  }

  private truncateCurrentPageToA4(content: string): void {
    if (!this.editor?.html?.set) {
      return;
    }

    const truncatedContent = this.truncateContentToA4(content);
    this.preventAutoPaste = true;
    this.editor.html.set(truncatedContent);
    normalizeFroalaTableVerticalAlignCells(this.editor);
    this.preventAutoPaste = false;

    const currentContent = this.editor.html.get();
    this.contentHeight = this.getMeasuredReportContentHeight(currentContent);
    this.allowTyping = this.contentHeight <= this.getReportSafePageHeight();
    this.loadedContentExceedsA4 = !this.allowTyping;
    this.hasUserEditedSinceLoad = true;
    this.lastKnownContent = currentContent;
    this.lastValidA4Content = this.allowTyping ? currentContent : '';
    this.updatePageContent(currentContent);

    if (this.allowTyping) {
      this.showA4TruncatedMessage();
      this.moveCaretToEndOfEditor();
    } else {
      this.setA4StatusMessage(
        'This saved content still exceeds A4 page size. Please delete more content before editing.',
        'warning',
        false
      );
    }
  }

  private truncateContentToA4(content: string): string {
    const container = document.createElement('div');
    container.innerHTML = content || '';
    let attempts = 0;

    while (container.innerHTML && this.wouldContentExceedA4(container.innerHTML) && attempts < 500) {
      if (!this.removeLastTruncatableContentNode(container)) {
        break;
      }
      attempts++;
    }

    return container.innerHTML;
  }

  private removeLastTruncatableContentNode(parent: Node): boolean {
    for (let index = parent.childNodes.length - 1; index >= 0; index--) {
      const node = parent.childNodes[index];

      if (node.nodeType === Node.TEXT_NODE) {
        parent.removeChild(node);
        return true;
      }

      if (node instanceof HTMLElement) {
        if (node.matches('img, table, hr, iframe, video, svg')) {
          node.remove();
          return true;
        }

        if (node.childNodes.length && this.removeLastTruncatableContentNode(node)) {
          if (this.isEmptyReportNode(node)) {
            node.remove();
          }
          return true;
        }

        node.remove();
        return true;
      }

      parent.removeChild(node);
      return true;
    }

    return false;
  }

  private moveCaretToEndOfEditor(): void {
    if (!this.editor?.el) {
      return;
    }

    const placeCaretAtEnd = () => {
      if (!this.editor?.el) {
        return;
      }

      this.editor.el.focus({ preventScroll: true });

      if (this.editor.selection?.setAtEnd && this.editor.selection?.restore) {
        this.editor.selection.setAtEnd(this.editor.el);
        this.editor.selection.restore();
        return;
      }

      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      const range = document.createRange();
      range.selectNodeContents(this.editor.el);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    };

    // The ngModel/Froala write-back after an html.set() rollback can reset the
    // caret to the top (worst on first interaction), so re-apply across those
    // cycles until the caret reliably lands at the end of the retained content.
    placeCaretAtEnd();
    setTimeout(placeCaretAtEnd, 0);
    requestAnimationFrame(() => requestAnimationFrame(placeCaretAtEnd));
    setTimeout(placeCaretAtEnd, 60);
  }

  private focusEditorForTyping(): void {
    const focusEditor = () => {
      if (!this.editorInitialized || !this.editor?.el) {
        return;
      }

      this.moveCaretToEndOfEditor();
    };

    focusEditor();
    setTimeout(focusEditor, 0);
    requestAnimationFrame(() => requestAnimationFrame(focusEditor));
    setTimeout(focusEditor, 180);
    setTimeout(focusEditor, 320);
  }

  /**
   * Check if content would exceed A4 size without modifying anything
   */
  private wouldContentExceedA4(content: string): boolean {
    if (!this.editor) return false;

    const height = this.getMeasuredReportContentHeight(content);

    return height > this.getReportSafePageHeight();
  }

  /**
   * Persist every page currently flagged as dirty. Used for deliberate
   * structural operations (delete / reorder) that renumber multiple pages.
   */
  private async persistDirtyPages(): Promise<void> {
    for (const page of this.pages) {
      if (!page.isDirty) {
        continue;
      }

      const saveRequest: SaveRequest = {
        pageId: page.id,
        content: page.content,
        pagesId: page.pagesId,
        timestamp: Date.now()
      };

      this.isSaving = true;
      try {
        await this.savePageToBackend(saveRequest);
        page.isDirty = false;
        page.savedContent = page.content;
      } catch (error) {
        console.error('Failed to persist page', page.id, error);
      } finally {
        this.isSaving = false;
      }
    }

    this.isUserTyping = false;
  }

  /**
   * Save page content to backend
   */
  private async savePageToBackend(saveRequest: SaveRequest): Promise<void> {
    const payload = {
      id: this.templateId,
      type: 3,
      template_type: this.title == this.translate.instant('RA_Static_Report_Templates') ? 1 : 
                     this.title == this.translate.instant('Quote_Templates') ? 2 : '',
      file_name: this.templateName,
      language: this.selectedLangauge,
      created_by: this.userInfo?.userId,
      is_active: true,
      is_new_template: false,
      page_list: [{
        static_template_pages_id: saveRequest.pagesId || '',
        static_template_id: this.templateId,
        page_no: saveRequest.pageId,
        static_content: this.prepareStaticContentForReport(
          saveRequest.content,
          Number(saveRequest.pageId) === Number(this.activePage)
        ),
        created_by: this.userInfo?.userId,
        is_active: true
      }]
    };

    return new Promise((resolve, reject) => {
      this.backendService.saveTemplate(payload).subscribe({
        next: (data: any) => {
          if (data.statusCode === 200) {
            this.templateId = data.response.id;
            this.saveTemplateResponse = data?.response;
            
            // Update page IDs from response
            const savedPage = data.response.page_list.find(p => Number(p?.page_no) === Number(saveRequest.pageId));
            if (savedPage) {
              const pageIndex = this.pages.findIndex(p => p.id === saveRequest.pageId);
              if (pageIndex !== -1) {
                this.pages[pageIndex].pagesId = savedPage.static_template_pages_id;
                // Track the last persisted content so we can revert on Discard
                this.pages[pageIndex].savedContent = saveRequest.content;
              }
            }
            resolve(data);
          } else if (data.statusCode === 409 || data.statusCode === 400) {
            const conflictMessage = data?.message?.toLowerCase?.().trim();
            const toastMessage = conflictMessage === 'file name already exists'
              ? 'File name already exists'
              : data?.message;
            this.toastService.presentToast(toastMessage, 4000, AppConstants.ERROR_SNACKBAR, 'top', 'error_occurred');
            reject(Object.assign(new Error(toastMessage), { handled: true }));
          } else {
            reject(new Error(this.translate.instant('error_occurred')));
          }
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  /**
   * Add a new page
   */
  public async addNewPage(): Promise<void> {
    if (this.isFileNameAndLanguageInvalid()) {
      this.toastService.presentToast(
        this.translate.instant('File name and language cannot be empty'), 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
      return;
    }

    // Centralized unsaved-changes guard for the current page before adding a new one
    const canProceed = await this.ensureCurrentPageSaved();
    if (!canProceed) {
      console.log('Add new page cancelled by unsaved-changes guard');
      return;
    }

    // Wait for any pending saves before adding new page
    if (this.isSaving) {
      await this.waitForSaveCompletion();
    }

    try {
      const newPage: Page = {
        id: this.nextPageId,
        title: `Page ${this.nextPageId}`,
        content: '',
        pagesId: '',
        isDirty: false,
        savedContent: ''
      };

      console.log('Adding new page:', newPage);
      
      // Add page to local array
      this.pages.push(newPage);
      this.nextPageId++;

      // Switch to the new page.
      await this.switchToPage(newPage.id);

      // Persist the new (blank) page to the backend immediately so it survives
      // even if the user exits without typing anything. Creating a page is a
      // deliberate structural action, so it saves right away (like delete /
      // reorder) rather than waiting for the manual Save button.
      this.isSaving = true;
      try {
        await this.savePageToBackend({
          pageId: newPage.id,
          content: newPage.content,
          pagesId: newPage.pagesId,
          timestamp: Date.now()
        });
        newPage.isDirty = false;
        newPage.savedContent = newPage.content;
        this.hasUserEditedSinceLoad = false;
      } finally {
        this.isSaving = false;
      }

      this.toastService.presentToast(this.translate.instant('new_page_added_successfully'), 2000, AppConstants.SUCCESS_SNACKBAR, 'top');
      
    } catch (error) {
      console.error('Error adding new page:', error);
      
      // Remove the page from local array if switching failed
      const pageIndex = this.pages.findIndex(p => p.id === this.nextPageId - 1);
      if (pageIndex !== -1) {
        this.pages.splice(pageIndex, 1);
        this.nextPageId--;
      }
      
      this.toastService.presentToast(
        'Error adding new page. Please try again.', 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
    }
  }

  /**
   * Wait for save completion with timeout
   */
  private async waitForSaveCompletion(timeoutMs: number = 5000): Promise<void> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const checkSave = () => {
        const elapsed = Date.now() - startTime;
        
        if (!this.isSaving) {
          console.log('Save completed, elapsed time:', elapsed + 'ms');
          resolve();
        } else if (elapsed > timeoutMs) {
          console.warn('Save operation timed out after', timeoutMs + 'ms');
          resolve(); // Resolve anyway to not block the UI
        } else {
          setTimeout(checkSave, 100);
        }
      };
      
      checkSave();
    });
  }

  /**
   * Delete a page
   */
  public async deletePage(pageId: number): Promise<void> {
    if (this.pages.length <= 1) {
      this.toastService.presentToast(
        this.translate.instant('page_required_msg'), 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
      return;
    }

    if (this.isFileNameAndLanguageInvalid()) {
      this.toastService.presentToast(
        'File name and language cannot be empty', 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
      return;
    }

    // Wait for any pending saves
    if (this.isSaving) {
      await this.waitForSaveCompletion();
    }

    const pageIndex = this.pages.findIndex(page => page.id === pageId);
    if (pageIndex === -1) return;

    const pageToDelete = this.pages[pageIndex];
    const wasActivePageDeleted = this.activePage === pageId;
    
    try {
      // If the page has a pagesId (exists on server), mark it as inactive via API
      if (pageToDelete.pagesId && pageToDelete.pagesId.trim() !== '') {
        console.log('Deleting page from server:', pageToDelete.pagesId);
        await this.deletePageFromServer(pageToDelete);
      } else {
        console.log('Page has no server ID, only removing locally');
      }

      // Remove the page from local array
      this.pages.splice(pageIndex, 1);

      // Reindex pages
      this.reindexPages();

      // If the active page was deleted, load the page that moved into its place.
      if (wasActivePageDeleted) {
        const replacementPageIndex = Math.min(pageIndex, this.pages.length - 1);
        const newActivePageId = this.pages[replacementPageIndex]?.id || 1;

        if (this.activePage === newActivePageId) {
          this.loadCurrentPageContent();
        } else {
          await this.switchToPage(newActivePageId);
        }
      }

      // Update template data and save changes
      this.updateTemplateData();
      
      // Reindexing changed page numbers, so persist the affected pages now
      // (deliberate structural change) instead of leaving them dirty.
      this.pages.forEach(page => page.isDirty = true);
      await this.persistDirtyPages();
      
      this.toastService.presentToast(this.translate.instant('page_deleted_successfully'), 2000, AppConstants.SUCCESS_SNACKBAR, 'top');
      
    } catch (error) {
      console.error('Error deleting page:', error);
      this.toastService.presentToast(
        'Error deleting page. Please try again.', 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
    }
  }

  /**
   * Delete page from server by marking it as inactive
   */
  private async deletePageFromServer(page: Page): Promise<void> {
    const payload = {
      id: this.templateId,
      type: 3,
      template_type: this.title == this.translate.instant('RA_Static_Report_Templates') ? 1 : 
                     this.title == this.translate.instant('Quote_Templates') ? 2 : '',
      file_name: this.templateName,
      language: this.selectedLangauge,
      created_by: this.userInfo?.userId,
      is_active: true,
      is_new_template: false,
      page_list: [{
        static_template_pages_id: page.pagesId,
        static_template_id: this.templateId,
        page_no: page.id,
        static_content: page.content,
        created_by: this.userInfo?.userId,
        is_active: false  // Mark as inactive to delete
      }]
    };

    return new Promise((resolve, reject) => {
      this.backendService.saveTemplate(payload).subscribe({
        next: (data: any) => {
          if (data.statusCode === 200) {
            console.log('Page marked as inactive on server successfully');
            resolve(data);
          } else if (data.statusCode === 409 || data.statusCode === 400) {
            console.error('Server error deleting page:', data?.message);
            reject(new Error(data?.message));
          } else {
            reject(new Error('Unknown error occurred while deleting page'));
          }
        },
        error: (error) => {
          console.error('API error deleting page:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Reindex pages after deletion
   */
  private reindexPages(): void {
    this.pages.forEach((page, index) => {
      const newId = index + 1;
      if (page.id !== newId) {
        // Update active page if it matches the old ID
        if (this.activePage === page.id) {
          this.activePage = newId;
        }
        
        console.log(`Reindexing page from ${page.id} to ${newId}`);
        page.id = newId;
        page.title = `Page ${newId}`;
        page.isDirty = true; // Mark as dirty since ID changed
      }
    });
    this.nextPageId = this.pages.length + 1;
    
    console.log('Pages reindexed. New structure:', this.pages.map(p => ({
      id: p.id,
      title: p.title,
      pagesId: p.pagesId,
      isDirty: p.isDirty
    })));
  }

  /**
   * Update template data structure
   */
  private updateTemplateData(): void {
    if (this.templateData && this.templateData.page_list) {
      this.templateData.page_list = this.pages.map(page => ({
        static_template_pages_id: page.pagesId,
        static_template_id: this.templateId,
        page_no: page.id,
        static_content: page.content,
        created_by: this.userInfo?.userId,
        is_active: true
      }));
    }
  }

  /**
   * Switch to a specific page
   */
  public async switchToPage(pageId: number): Promise<void> {
    if (this.switchingLock || this.activePage === pageId) {
      return;
    }

    // Safety check - ensure editor is initialized
    if (!this.editorInitialized || !this.editor) {
      console.warn('Editor not initialized, cannot switch page');
      return;
    }

    this.switchingLock = true;
    this.isPageSwitching = true;
    this.preventAutoPaste = true;

    try {
      // Manual-save mode: any unsaved changes on the current page are handled by
      // the centralized guard (ensureCurrentPageSaved) before we get here, so we
      // simply switch and load the target page without triggering a save.

      // Switch to new page
      this.activePage = pageId;
      this.loadCurrentPageContent();

      // Brief delay to allow editor to settle
      setTimeout(() => {
        this.isPageSwitching = false;
        this.preventAutoPaste = false;
        this.switchingLock = false;
        this.focusEditorForTyping();
        // Do NOT validate image integrity during page switch - this causes URL mixing
        // The images should remain exactly as saved in the content
      }, 300);

    } catch (error) {
      console.error('Error switching pages:', error);
      this.isPageSwitching = false;
      this.preventAutoPaste = false;
      this.switchingLock = false;
    }
  }

  /**
   * Handle page change from UI
   */
  public async changePage(fromPageId: number, toPageId: number): Promise<void> {
    console.log('changePage called:', { 
      fromPageId, 
      toPageId, 
      editorInitialized: this.editorInitialized, 
      hasEditor: !!this.editor,
      hasHtmlMethods: !!(this.editor?.html?.get && this.editor?.html?.set)
    });
    
    // Simplified check - just verify basic editor availability like before
    if (!this.editorInitialized || !this.editor) {
      console.warn('Editor not ready, cannot switch pages');
      console.log('Editor status:', { 
        editorInitialized: this.editorInitialized, 
        hasEditor: !!this.editor,
        hasHtmlMethods: !!(this.editor?.html?.get && this.editor?.html?.set)
      });
      this.toastService.presentToast(this.translate.instant('editor_loading_please_wait'), 2000, AppConstants.WARNING_SNACKBAR, 'top');
      return;
    }

    // Centralized unsaved-changes guard (Save / Discard / Cancel)
    const canProceed = await this.ensureCurrentPageSaved();
    if (!canProceed) {
      console.log('Page switch cancelled by unsaved-changes guard');
      return;
    }

    console.log('All checks passed, proceeding with page switch');
    await this.switchToPage(toPageId);
  }

  /**
   * Delete page confirmation
   */
  public async deleteConfirmation(pageId: number): Promise<void> {
    const itemName = this.translate.instant('page') + ' ' + pageId;
    const title = this.translate.instant('Are_you_sure_you_want_to_delete') + '?';
    const subtitle = `'${itemName}'`;
    const actionText = this.translate.instant('Delete');
    const isConfirmed = await this.confirmationService.presentConfirmationModal(
      title,
      actionText,
      true,
      this.translate.instant('Cancel'),
      subtitle
    );
    
    if (isConfirmed) {
      await this.deletePage(pageId);
    }
  }

  /**
   * Handle drag and drop reordering
   */
  public async drop(event: CdkDragDrop<string[]>): Promise<void> {
    if (this.isFileNameAndLanguageInvalid()) {
      this.toastService.presentToast(
        'File name and language cannot be empty', 
        4000, 
        AppConstants.ERROR_SNACKBAR, 
        'top'
      );
      return;
    }

    if (event.previousIndex !== event.currentIndex) {
      try {
        // Wait for any pending saves
        if (this.isSaving) {
          await this.waitForSaveCompletion();
        }

        console.log('Reordering pages:', { from: event.previousIndex, to: event.currentIndex });
        
        moveItemInArray(this.pages, event.previousIndex, event.currentIndex);
        this.reindexPages();
        this.updateTemplateData();

        // Reindexing changed page numbers, so persist the affected pages now
        // (deliberate structural change) instead of leaving them dirty.
        this.pages.forEach(page => page.isDirty = true);
        await this.persistDirtyPages();
        
        this.toastService.presentToast(this.translate.instant('pages_reordered_successfully'), 2000, AppConstants.SUCCESS_SNACKBAR, 'top');
        
      } catch (error) {
        console.error('Error reordering pages:', error);
        this.toastService.presentToast(
          'Error reordering pages. Please try again.', 
          4000, 
          AppConstants.ERROR_SNACKBAR, 
          'top'
        );
      }
    }
  }

  /**
   * Update page title
   */
  public updatePageTitle(pageId: number, newTitle: string): void {
    const pageIndex = this.pages.findIndex(page => page.id === pageId);
    if (pageIndex !== -1) {
      this.pages[pageIndex].title = newTitle;
      this.pages[pageIndex].isDirty = true;
    }
  }

  /**
   * Handle language change
   */
  public onChangeLanguage(item: string): void {
    this.selectedLangauge = item;
    this.languageChanged = true;

    this.enableLanguageClearKeyboardNavigation();
    
    // Mark current page as dirty so the manual Save persists the change
    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (currentPage) {
      currentPage.isDirty = true;
    }
  }

  public onLanguageClearKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const clearIcon = target?.closest('.ng-clear') as HTMLElement | null;

    if (!clearIcon) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.onLanguageCleared();
  }

  public onLanguageCleared(): void {
    this.selectedLangauge = null;
    this.templateForm?.get('langauge')?.setValue(null);
    this.languageChanged = true;

    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (currentPage) {
      currentPage.isDirty = true;
    }

    this.enableLanguageClearKeyboardNavigation();
  }

  private enableLanguageClearKeyboardNavigation(): void {
    setTimeout(() => {
      const selectHost = this.languageSelectRef?.nativeElement;
      const clearIcon = selectHost?.querySelector('.ng-clear') as HTMLElement | null;

      if (!clearIcon) {
        return;
      }

      clearIcon.setAttribute('tabindex', '0');
      clearIcon.setAttribute('role', 'button');
      clearIcon.setAttribute('aria-label', 'Clear language');

      if (this.languageClearKeyHandlers.has(clearIcon)) {
        return;
      }

      clearIcon.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.onLanguageCleared();
      }, true);

      clearIcon.addEventListener('focus', (event: FocusEvent) => {
        event.stopPropagation();
      }, true);

      clearIcon.addEventListener('click', (event: MouseEvent) => {
        event.stopPropagation();
      }, true);

      this.languageClearKeyHandlers.add(clearIcon);
    }, 0);
  }

  /**
   * Handle name change
   */
  public onChangeName(): void {
    // Mark current page as dirty so the manual Save persists the change
    this.isNameDirty = true;
    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (currentPage) {
      currentPage.isDirty = true;
    }
  }

  /**
   * Handle content change from template binding
   */
  public onContentChange(content: string): void {
    if (!this.isPageSwitching && !this.preventAutoPaste) {
      this.checkContentSize(content);
    }
  }

  /**
   * Exit the editor
   */
  public async exit(): Promise<void> {
    if (this.isFileNameAndLanguageInvalid()) {
      if (this.hasWhitespaceOnlyTemplateNameOrLanguage()) {
        this.showMandatoryTemplateValidationToast();
      }
      return;
    }

    // Centralized unsaved-changes guard before leaving the editor
    const canProceed = await this.ensureCurrentPageSaved();
    if (!canProceed) {
      console.log('Exit cancelled by unsaved-changes guard');
      return;
    }

    // Emit exit event once any unsaved changes have been handled
    this.exitEvent.emit(this.saveTemplateResponse);
  }

  /**
   * Save current template manually (triggered by the form submit / Enter key)
   */
  public async saveCurrentTemplate(): Promise<void> {
    await this.saveCurrentPage();
  }

  /**
   * Returns true when the current page has unsaved changes.
   * Used to enable/disable the Save button and drive the status indicator.
   */
  public isCurrentPageDirty(): boolean {
    const currentPage = this.pages.find(page => page.id === this.activePage);
    return !!currentPage?.isDirty;
  }

  /**
   * Expose the in-progress save flag to the template.
   */
  public isSaveInProgress(): boolean {
    return this.isSaving;
  }

  /**
   * Pull the latest editor content into the current page model and flag it
   * dirty when it differs from what we last stored.
   */
  private syncEditorContentToCurrentPage(): void {
    if (!this.editorInitialized || !this.editor || !this.editor.html || typeof this.editor.html.get !== 'function') {
      return;
    }

    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (!currentPage) {
      return;
    }

    const latestContent = this.stripTransientEditorSelectionClasses(this.editor.html.get());
    currentPage.content = latestContent;
    // Only dirty when the user genuinely edited and the normalized content
    // differs from the loaded/saved baseline (ignores focus-only re-normalization).
    // Also preserve dirty state when only the template name was changed.
    const contentIsDirty = this.hasUserEditedSinceLoad &&
      this.normalizeHtmlForComparison(latestContent) !== this.activePageBaselineContent;
    currentPage.isDirty = this.isNameDirty || contentIsDirty;
  }

  /**
   * Manually save the current page to the backend (Save button / form submit).
   * Returns true when the page is persisted (or there was nothing to save).
   */
  public async saveCurrentPage(): Promise<boolean> {
    if (this.isFileNameAndLanguageInvalid()) {
      if (this.hasWhitespaceOnlyTemplateNameOrLanguage()) {
        this.showMandatoryTemplateValidationToast();
      } else {
        this.toastService.presentToast(
          this.translate.instant('File name and language cannot be empty'),
          4000,
          AppConstants.ERROR_SNACKBAR,
          'top'
        );
      }
      return false;
    }

    this.syncEditorContentToCurrentPage();

    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (!currentPage) {
      return false;
    }

    if (!currentPage.isDirty) {
      // Nothing changed since the last save
      this.toastService.presentToast(this.translate.instant('Content saved') + '.', 2000, AppConstants.SUCCESS_SNACKBAR, 'top');
      return true;
    }

    if (this.isSaving) {
      await this.waitForSaveCompletion();
    }

    this.isSaving = true;
    try {
      const saveRequest: SaveRequest = {
        pageId: currentPage.id,
        content: currentPage.content,
        pagesId: currentPage.pagesId,
        timestamp: Date.now()
      };

      await this.savePageToBackend(saveRequest);

      currentPage.isDirty = false;
      currentPage.savedContent = currentPage.content;
      this.activePageBaselineContent = this.normalizeHtmlForComparison(currentPage.content);
      this.hasUserEditedSinceLoad = false;
      this.isNameDirty = false;
      this.isUserTyping = false;

      this.toastService.presentToast(this.translate.instant('Content saved') + '.', 2000, AppConstants.SUCCESS_SNACKBAR, 'top');
      return true;
    } catch (error: any) {
      console.error('Manual save failed:', error);
      // savePageToBackend already surfaces validation/conflict toasts (handled flag)
      if (!error?.handled) {
        this.toastService.presentToast(this.translate.instant('failed_to_save_data'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
      }
      return false;
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Centralized unsaved-changes guard used before switching pages, adding a
   * page, or exiting the editor. Returns true when it is safe to proceed.
   */
  private async ensureCurrentPageSaved(): Promise<boolean> {
    // Make sure the in-memory model reflects the latest editor content
    this.syncEditorContentToCurrentPage();

    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (!currentPage || !currentPage.isDirty) {
      return true; // Nothing to save, safe to proceed
    }

    const choice = await this.confirmationService.presentUnsavedChangesModal();

    if (choice === 'save') {
      return await this.saveCurrentPage();
    }

    if (choice === 'discard') {
      this.revertCurrentPageToSaved();
      return true;
    }

    // 'cancel' - stay on the current page
    return false;
  }

  /**
   * Revert the current page back to the last content persisted to the backend.
   */
  private revertCurrentPageToSaved(): void {
    const currentPage = this.pages.find(page => page.id === this.activePage);
    if (!currentPage) {
      return;
    }

    currentPage.content = currentPage.savedContent || '';
    currentPage.isDirty = false;
    this.activePageBaselineContent = this.normalizeHtmlForComparison(currentPage.content);
    this.hasUserEditedSinceLoad = false;
    this.isNameDirty = false;
    this.isUserTyping = false;

    if (this.editorInitialized && this.editor && this.editor.html && typeof this.editor.html.set === 'function') {
      this.preventAutoPaste = true;
      this.editor.html.set(currentPage.content);
      this.lastKnownContent = currentPage.content;
      setTimeout(() => {
        this.preventAutoPaste = false;
      }, 100);
    }
  }

  /**
   * Check if file name and language are valid
   */
  public isFileNameAndLanguageInvalid(): boolean {
    const templateName = typeof this.templateName === 'string' ? this.templateName : '';
    const selectedLanguage = typeof this.selectedLangauge === 'string' ? this.selectedLangauge : '';

    return (
      !templateName || templateName.trim() === '' ||
      !selectedLanguage || selectedLanguage.trim() === ''
    );
  }

  private hasWhitespaceOnlyTemplateNameOrLanguage(): boolean {
    const templateName = typeof this.templateName === 'string' ? this.templateName : '';
    const selectedLanguage = typeof this.selectedLangauge === 'string' ? this.selectedLangauge : '';

    return (templateName.length > 0 && templateName.trim().length === 0) ||
      (selectedLanguage.length > 0 && selectedLanguage.trim().length === 0);
  }

  private showMandatoryTemplateValidationToast(): void {
    this.toastService.presentToast(
      this.translate.instant('only_spaces_are_not_allowed'),
      4000,
      AppConstants.ERROR_SNACKBAR,
      'top'
    );
  }

  /**
   * Debug method to check editor status - can be called from console
   */
  public debugEditorStatus(): void {
    console.log('=== EDITOR DEBUG STATUS ===');
    console.log('editorInitialized:', this.editorInitialized);
    console.log('editor object exists:', !!this.editor);
    console.log('editor object:', this.editor);
    console.log('isEditorReady():', this.isEditorReady());
    
    if (this.editor) {
      console.log('editor type:', typeof this.editor);
      console.log('editor keys:', Object.keys(this.editor));
      console.log('editor._editor.html:', this.editor._editor.html);
      console.log('editor._editor.html type:', typeof this.editor._editor.html);

      if (this.editor._editor.html) {
        console.log('html keys:', Object.keys(this.editor._editor.html));
        console.log('html.get type:', typeof this.editor._editor.html.get);
        console.log('html.set type:', typeof this.editor._editor.html.set);
      }
      
      // Test if we can get content
      try {
        if (this.editor._editor.html?.get) {
          const content = this.editor._editor.html.get();
          console.log('Current content length:', content?.length || 0);
        }
      } catch (error) {
        console.log('Error getting content:', error);
      }
      
      // Check image upload configuration
      console.log('=== IMAGE UPLOAD CONFIG ===');
      console.log('imageUpload enabled:', this.editor.opts?.imageUpload);
      console.log('imageUploadURL:', this.editor.opts?.imageUploadURL);
      console.log('imageUploadMethod:', this.editor.opts?.imageUploadMethod);
      console.log('imageUploadParam:', this.editor.opts?.imageUploadParam);
      console.log('imageUploadParams:', this.editor.opts?.imageUploadParams);
      console.log('imageMaxSize:', this.editor.opts?.imageMaxSize);
      console.log('imageAllowedTypes:', this.editor.opts?.imageAllowedTypes);
    }
    
    console.log('pages count:', this.pages.length);
    console.log('active page:', this.activePage);
    console.log('=== END DEBUG ===');
  }

  /**
   * Force editor initialization (for debugging/recovery)
   */
  public forceEditorInitialization(): void {
    console.log('=== FORCING EDITOR INITIALIZATION ===');
    
    if (!this.editor) {
      console.error('No editor object available to initialize');
      return;
    }
    
    console.log('Attempting to force editor initialization...');
    this.editorInitialized = false; // Reset flag
    this.waitForEditorMethods(); // Re-run initialization
    
    console.log('Force initialization triggered');
  }

  /**
   * Test image upload configuration
   */
  public testImageUpload(): void {
    console.log('=== IMAGE UPLOAD TEST ===');
    console.log('Upload URL:', urlPathConstants.imageUploadUrl);
    console.log('User ID:', this.userInfo?.userId);
    console.log('Active Page:', this.activePage);
    console.log('Editor Options Image Config:', {
      imageUpload: this.options['imageUpload'],
      imageUploadURL: this.options['imageUploadURL'],
      imageUploadMethod: this.options['imageUploadMethod'],
      imageUploadParam: this.options['imageUploadParam'],
      imageUploadParams: this.options['imageUploadParams']
    });

    if (this.editor && this.editor.opts) {
      console.log('Runtime Editor Image Config:', {
        imageUpload: this.editor.opts.imageUpload,
        imageUploadURL: this.editor.opts.imageUploadURL,
        imageUploadMethod: this.editor.opts.imageUploadMethod,
        imageUploadParam: this.editor.opts.imageUploadParam,
        imageUploadParams: this.editor.opts.imageUploadParams
      });
    }
    console.log('=== END IMAGE UPLOAD TEST ===');
  }

  /**
   * Debug page management - can be called from console
   */
  public debugPageManagement(): void {
    console.log('=== PAGE MANAGEMENT DEBUG ===');
    console.log('Template ID:', this.templateId);
    console.log('Template Name:', this.templateName);
    console.log('Selected Language:', this.selectedLangauge);
    console.log('Total Pages:', this.pages.length);
    console.log('Active Page:', this.activePage);
    console.log('Next Page ID:', this.nextPageId);
    
    console.log('Pages Details:');
    this.pages.forEach((page, index) => {
      console.log(`Page ${index + 1}:`, {
        id: page.id,
        title: page.title,
        pagesId: page.pagesId,
        contentLength: page.content?.length || 0,
        isDirty: page.isDirty
      });
    });
    
    if (this.templateData && this.templateData.page_list) {
      console.log('Template Data Pages:', this.templateData.page_list.length);
      this.templateData.page_list.forEach((page, index) => {
        console.log(`Template Page ${index + 1}:`, {
          static_template_pages_id: page.static_template_pages_id,
          page_no: page.page_no,
          is_active: page.is_active,
          contentLength: page.static_content?.length || 0
        });
      });
    }
    
    console.log('=== END PAGE MANAGEMENT DEBUG ===');
  }

  /**
   * Test adding a new page (for debugging)
   */
  public async testAddPage(): Promise<void> {
    console.log('=== TESTING ADD PAGE ===');
    console.log('Current pages before add:', this.pages.length);
    await this.addNewPage();
    console.log('Current pages after add:', this.pages.length);
    console.log('=== END TEST ADD PAGE ===');
  }

  /**
   * Trigger image upload for pasted images using proper Froala upload flow
   * This method converts data URI images to files and triggers upload via beforeUpload event
   */
  private triggerImageUploadForPastedImages(images: NodeListOf<Element>): void {
    console.log('=== TRIGGERING UPLOAD FOR PASTED IMAGES ===');
    console.log('Processing', images.length, 'pasted images');
    
    images.forEach((img, index) => {
      const imgElement = img as HTMLImageElement;
      const dataUrl = imgElement.src;
      
      if (!dataUrl.startsWith('data:')) {
        console.log(`Image ${index + 1} is not a data URI, skipping`);
        return;
      }
      
      try {
        console.log(`Processing pasted image ${index + 1}:`, dataUrl.substring(0, 50) + '...');
        
        // Convert data URL to file
        const file = this.dataURLtoFile(dataUrl, `pasted-image-${Date.now()}-${index}`);
        
        if (file) {
          console.log('Created file for pasted image:', {
            name: file.name,
            size: file.size,
            type: file.type
          });
          
          // Create a FileList-like object
          const fileArray = [file];
          
          // Set flag to indicate this is a paste operation  
          this.pendingPasteImage = true;
          
          // Use Froala's image upload API which should trigger our beforeUpload handler
          if (this.editor && this.editor.image && typeof this.editor.image.upload === 'function') {
            console.log('Triggering image upload via Froala API for pasted image', this.editor);
            try {
              this.editor.image.upload(fileArray);
            } catch (uploadError) {
              console.error('Froala image upload failed:', uploadError);
              // Remove the data URI image since we couldn't upload it
              imgElement.remove();
              this.toastService.presentToast(this.translate.instant('upload_failed'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          } else {
            console.warn('Froala image upload API not available');
            // Remove the data URI image since we can't upload it
            imgElement.remove();
            this.toastService.presentToast(this.translate.instant('upload_failed'), 3000, AppConstants.ERROR_SNACKBAR, 'top');
          }
        }
      } catch (error) {
        console.error(`Error processing pasted image ${index + 1}:`, error);
      }
    });
  }

  /**
   * Convert data URL to File object
   */
  private dataURLtoFile(dataURL: string, filename: string): File | null {
    try {
      const arr = dataURL.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) {
        console.error('Could not extract MIME type from data URL');
        return null;
      }
      
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      
      // Add proper file extension
      const extension = mime.split('/')[1] || 'png';
      const fullFilename = filename.includes('.') ? filename : `${filename}.${extension}`;
      
      return new File([u8arr], fullFilename, { type: mime });
    } catch (error) {
      console.error('Error converting data URL to file:', error);
      return null;
    }
  }

  /**
   * Create a FileList-like object from an array of files
   */
  private createFileList(files: File[]): FileList {
    const dt = new DataTransfer();
    files.forEach(file => dt.items.add(file));
    return dt.files;
  }

  /**
   * Monitor image upload progress for pasted images
   */
  private monitorImageUploadProgress(images: NodeListOf<Element>, checkCount: number): void {
    const maxChecks = 4; // Reduced from 6 to 4 checks (2 seconds total)
    
    if (checkCount >= maxChecks) {
      console.log('⏳ Image upload monitoring complete - letting normal flow handle any remaining issues');
      // Don't trigger manual upload here - let the normal flow handle it
      return;
    }

    setTimeout(() => {
      // Check if uploads have started (should be reflected in our tracking flags)
      if (this.imageUploadInProgress || this.pastedImageUploadInProgress) {
        console.log('✅ Image upload process detected - uploads are in progress');
        return; // Upload process is working, let it complete
      }

      // Check if images are still data URIs
      const remainingDataImages = this.editor.el.querySelectorAll('img[src^="data:"]');
      if (remainingDataImages.length === 0) {
        console.log('✅ All data URI images have been processed successfully');
        return; // All images processed
      }

      if (remainingDataImages.length < images.length) {
        console.log(`🔄 Progress: ${images.length - remainingDataImages.length}/${images.length} images processed`);
      }

      // Continue monitoring
      this.monitorImageUploadProgress(images, checkCount + 1);
    }, 500);
  }

  /**
   * Check and handle pasted images (called when editor becomes ready)
   */
  private checkAndHandlePastedImages(): void {
    if (!this.editor || !this.editor.el) {
      console.warn('Editor or editor element not available');
      return;
    }

    const dataUriImages = this.editor.el.querySelectorAll('img[src^="data:"]');
    if (dataUriImages.length > 0) {
      console.log(`Found ${dataUriImages.length} data URI images after editor initialization`);
      this.monitorImageUploadProgress(dataUriImages, 0);
    }
  }

  /**
   * Handle pasted images (legacy method - now replaced by triggerImageUploadForPastedImages)
   * This method processes data URI images that weren't automatically uploaded
   */
  private handlePastedImages(images: NodeListOf<Element>): void {
    console.log('Processing', images.length, 'pasted images for manual upload');
    
    images.forEach((img, index) => {
      const imgElement = img as HTMLImageElement;
      const dataUrl = imgElement.src;
      
      if (dataUrl.startsWith('data:')) {
        console.log(`Processing pasted image ${index + 1}:`, dataUrl.substring(0, 50) + '...');
        
        try {
          // Validate data URL format
          if (!dataUrl.includes(',')) {
            console.error('Invalid data URL format - missing comma separator');
            return;
          }
          
          // Convert data URL to blob
          const [header, data] = dataUrl.split(',');
          
          // Validate header
          if (!header || !data) {
            console.error('Invalid data URL - missing header or data section');
            return;
          }
          
          const mimeMatch = header.match(/data:([^;]+)/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
          
          // Validate that it's actually an image MIME type
          if (!mimeType.startsWith('image/')) {
            console.error('Data URL is not an image type:', mimeType);
            return;
          }
          
          // Validate base64 data
          if (!data || data.length === 0) {
            console.error('Empty base64 data in data URL');
            return;
          }
          
          // Try to decode base64 data
          let byteCharacters;
          try {
            byteCharacters = atob(data);
          } catch (atobError) {
            console.error('Failed to decode base64 data:', atobError);
            console.error('Data length:', data.length);
            console.error('First 100 chars of data:', data.substring(0, 100));
            return;
          }
          
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          
          // Validate blob size
          if (blob.size === 0) {
            console.error('Created blob has zero size');
            return;
          }
          
          // Create a File object
          const fileName = `pasted-image-${Date.now()}-${index}.${mimeType.split('/')[1]}`;
          const file = new File([blob], fileName, { type: mimeType });
          
          console.log('Created file for pasted image:', {
            name: file.name,
            size: file.size,
            type: file.type
          });
          
          // Manually trigger upload using Froala's upload method
          if (this.editor && this.editor.image) {
            console.log('Attempting to upload pasted image via Froala API...');
            this.editor.image.upload([file]);
          } else {
            console.warn('Froala image upload API not available');
          }
          
        } catch (error) {
          console.error('Error processing pasted image:', error);
          console.error('Data URL that caused error:', dataUrl.substring(0, 100) + '...');
        }
      }
    });
  }

  /**
   * Debug image paste functionality
   */
  public debugImagePaste(): void {
    console.log('=== DEBUG IMAGE PASTE ===');
    console.log('imagePaste enabled:', this.editor?.opts?.imagePaste);
    console.log('imagePasteProcess enabled:', this.editor?.opts?.imagePasteProcess);
    console.log('imageUpload enabled:', this.editor?.opts?.imageUpload);
    console.log('imageUploadURL:', this.editor?.opts?.imageUploadURL);
    
    // Check for data URI images that might not have been uploaded
    if (this.editor && this.editor.el) {
      const dataImages = this.editor.el.querySelectorAll('img[src^="data:"]');
      console.log('Data URI images found:', dataImages.length);
      
      dataImages.forEach((img, index) => {
        console.log(`Data image ${index + 1}:`, img.src.substring(0, 50) + '...');
      });
      
      const uploadedImages = this.editor.el.querySelectorAll('img:not([src^="data:"])');
      console.log('Uploaded images found:', uploadedImages.length);
    }
    
    console.log('=== END DEBUG IMAGE PASTE ===');
  }

  /**
   * Check if there are any unsaved changes (for debugging and UI)
   */
  public checkUnsavedChanges(): boolean {
    try {
      // Check if any page is dirty
      const dirtyPages = this.pages.filter(page => page.isDirty);
      
      // Check if current editor content differs from saved content
      let currentContentDiffers = false;
      if (this.editorInitialized && this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
        const currentPage = this.pages.find(page => page.id === this.activePage);
        if (currentPage) {
          const currentContent = this.editor.html.get();
          currentContentDiffers = currentPage.content !== currentContent;
        }
      }
      
      const hasUnsavedChanges = dirtyPages.length > 0 || currentContentDiffers || this.isSaving;
      
      console.log('Unsaved changes check:', {
        dirtyPagesCount: dirtyPages.length,
        dirtyPages: dirtyPages.map(p => ({ id: p.id, title: p.title })),
        currentContentDiffers,
        isSaving: this.isSaving,
        hasUnsavedChanges,
        pageSwitchBlocked: this.pageSwitchBlocked,
        hasUnsavedContent: this.hasUnsavedContent
      });
      
      return hasUnsavedChanges;
    } catch (error) {
      console.error('Error checking unsaved changes:', error);
      return false;
    }
  }

  /**
   * Check page switching status (for debugging)
   */
  public checkPageSwitchStatus(): object {
    const status = {
      pageSwitchBlocked: this.pageSwitchBlocked,
      hasUnsavedContent: this.hasUnsavedContent,
      isSaving: this.isSaving,
      isPageSwitching: this.isPageSwitching,
      switchingLock: this.switchingLock,
      editorInitialized: this.editorInitialized,
      activePage: this.activePage,
      canSwitchPages: !this.pageSwitchBlocked && !this.hasUnsavedContent && !this.isSaving
    };
    
    console.log('Page Switch Status:', status);
    return status;
  }

  /**
   * Set default font for the editor
   */
  private setDefaultFont(): void {
    if (this.editorInitialized) {
      const editorContent = document.querySelector('#editor_froala .fr-view') as HTMLElement;
      if (editorContent) {
        editorContent.style.fontFamily = 'var(--font-NeueHaasDisplayLight)';
      }
    }
  }

  /**
   * Highlight placeholders in the content
   */
  private highlightPlaceholders(): void {
    if (!this.editor || !this.editorInitialized) {
      console.warn('Cannot highlight placeholders - editor not ready');
      return;
    }

    try {
      const allowed = [
        'CustomerName', 'ProjectName', 'CustomerStreet', 'CustomerHouse',
        'CustomerCity', 'CustomerZipCode', 'CustomerCountry', 'Date',
        'CompanyStreet', 'CompanyHouse', 'CompanyCity', 'CompanyZipCode',
        'CompanyCountry', 'CompanyName', 'NameOfMachine', 'CustomerContactName',
        'CustomerContactEmail', 'CustomerContactFon'
      ];

      const regex = new RegExp('\\{\\{(' + allowed.join('|') + ')\\}\\}', 'g');
      
      // Safety check before getting HTML
      if (!this.editor.html || typeof this.editor.html.get !== 'function') {
        console.warn('Editor html.get method not available');
        return;
      }

      const html = this.editor.html.get();

      // Remove existing highlighting
      const unhighlightedHtml = html.replace(/<span class="froala-highlighter">([\s\S]*?)<\/span>/g, '$1');
      
      // Add new highlighting
      const highlightedHtml = unhighlightedHtml.replace(regex, (match) => `<span class="froala-highlighter">${match}</span>`);

      if (highlightedHtml === html) {
        return;
      }
      
      // Safety check before setting HTML
      if (typeof this.editor.html.set === 'function') {
        const selection = window.getSelection();
        const selectionIsInEditor = !!selection?.rangeCount && this.editor.el.contains(selection.getRangeAt(0).commonAncestorContainer);

        if (selectionIsInEditor && this.editor.selection?.save && this.editor.selection?.restore) {
          this.editor.selection.save();
          const htmlWithSelectionMarkers = this.editor.html.get(true);
          const highlightedHtmlWithMarkers = htmlWithSelectionMarkers
            .replace(/<span class="froala-highlighter">([\s\S]*?)<\/span>/g, '$1')
            .replace(regex, (match) => `<span class="froala-highlighter">${match}</span>`);
          this.editor.html.set(highlightedHtmlWithMarkers);
          this.editor.selection.restore();
        } else {
          this.editor.html.set(highlightedHtml);
        }
      } else {
        console.warn('Editor html.set method not available');
      }
    } catch (error) {
      console.error('Error highlighting placeholders:', error);
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    
    // Final save before destroying
    if (this.editorInitialized && !this.isPageSwitching && this.editor) {
      const currentPage = this.pages.find(page => page.id === this.activePage);
      if (currentPage && this.editor && this.editor.html && typeof this.editor.html.get === 'function') {
        try {
          currentPage.content = this.editor.html.get();
          currentPage.isDirty = true;
          // Note: This is synchronous cleanup, async save might not complete
        } catch (error) {
          console.warn('Error getting content during cleanup:', error);
        }
      }
    }
    
    // Reset editor state
    this.editorInitialized = false;
    this.editor = null;
  }

  private cleanupImageReferences(): void {
    try {
      if (this.editor) {
        // Get all images in the editor
        const images = this.editor._editor.el.querySelectorAll('img');

        images.forEach((img, index) => {
          // Only log for debugging - DO NOT modify URLs during cleanup
          const src = img.src;
          const imageId = img.getAttribute('data-image-id');
          const pageId = img.getAttribute('data-page-id');
          
          console.log(`Image ${index}: src=${src}, id=${imageId}, page=${pageId}`);
          
          // DO NOT modify image sources during cleanup - this causes URL mixing
          // The URLs should remain as they were saved in the content
        });
      }
    } catch (error) {
      console.error('Error cleaning up image references:', error);
    }
  }

  private validateImageIntegrity(): void {
    try {
      if (this.editor) {
        const images = this.editor._editor.el.querySelectorAll('img');
        const imageUrls = new Set();
        
        images.forEach((img, index) => {
          const src = img.src;
          if (imageUrls.has(src)) {
            console.warn('Duplicate image URL detected:', src);
            // Log the duplicate but DO NOT modify the URL during page switch
            // URL modification during page switching causes the mixing issue
          } else {
            imageUrls.add(src);
          }
        });
      }
    } catch (error) {
      console.error('Error validating image integrity:', error);
    }
  }

  /**
   * Debug method to check content for data URI images
   * Call from console: window.froalaComponent.debugImageContent()
   */
  public debugImageContent(): void {
    console.log('=== DEBUGGING IMAGE CONTENT ===');
    
    if (!this.editor || !this.editor.html || typeof this.editor.html.get !== 'function') {
      console.error('Editor not ready');
      return;
    }
    
    const content = this.editor.html.get();
    console.log('Current content length:', content.length);
    
    // Check for images in content
    const allImages = content.match(/<img[^>]*>/g);
    console.log('Total images in content:', allImages ? allImages.length : 0);
    
    if (allImages) {
      allImages.forEach((img, index) => {
        console.log(`Image ${index + 1}:`, img);
        
        // Extract src
        const srcMatch = img.match(/src="([^"]+)"/);
        if (srcMatch) {
          const src = srcMatch[1];
          if (src.startsWith('data:')) {
            console.warn(`  ❌ Image ${index + 1} has data URI (${src.substring(0, 50)}...)`);
          } else if (src.includes('blob.core.windows.net')) {
            console.log(`  ✅ Image ${index + 1} has blob URL (${src.substring(0, 80)}...)`);
          } else {
            console.log(`  🔗 Image ${index + 1} has other URL (${src})`);
          }
        }
      });
    }
    
    // Check for data URI images specifically
    const dataUriImages = content.match(/src="data:[^"]+"/g);
    console.log('Data URI images found:', dataUriImages ? dataUriImages.length : 0);
    
    if (dataUriImages) {
      console.warn('⚠️ The following data URI images will be saved as base64 instead of blob URLs:');
      dataUriImages.forEach((dataUri, index) => {
        console.warn(`  ${index + 1}: ${dataUri.substring(0, 100)}...`);
      });
    } else {
      console.log('✅ No data URI images found - all images should save as URLs');
    }
    
    console.log('=== END DEBUG ===');
  }

  /**
   * Debug image upload response format
   * Call this method from console to test server response parsing
   */
  public debugImageUploadResponse(mockResponse: any): void {
    console.log('=== DEBUG IMAGE UPLOAD RESPONSE ===');
    console.log('Testing response:', mockResponse);
    console.log('Response type:', typeof mockResponse);
    
    let imageUrl = null;
    let parsedResponse = null;
    
    try {
      // Parse response if it's a string
      if (typeof mockResponse === 'string') {
        parsedResponse = JSON.parse(mockResponse);
        console.log('Parsed response object:', parsedResponse);
      } else {
        parsedResponse = mockResponse;
        console.log('Response is already an object:', parsedResponse);
      }
      
      // Extract image URL from different possible response structures
      if (parsedResponse) {
        imageUrl = parsedResponse.link || 
                  parsedResponse.url || 
                  parsedResponse.image_url || 
                  parsedResponse.file_url ||
                  parsedResponse.data?.url ||
                  parsedResponse.data?.link ||
                  parsedResponse.response?.url ||
                  parsedResponse.response?.link ||
                  parsedResponse.response?.image_url;
        
        console.log('Extracted image URL:', imageUrl);
        
        // Log all possible URL fields for debugging
        console.log('Response URL fields check:', {
          link: parsedResponse.link,
          url: parsedResponse.url,
          image_url: parsedResponse.image_url,
          file_url: parsedResponse.file_url,
          'data.url': parsedResponse.data?.url,
          'data.link': parsedResponse.data?.link,
          'response.url': parsedResponse.response?.url,
          'response.link': parsedResponse.response?.link,
          'response.image_url': parsedResponse.response?.image_url
        });
        
        // Log full response structure
        console.log('Full response structure:');
        console.log(JSON.stringify(parsedResponse, null, 2));
      }
      
      if (imageUrl) {
        console.log('✅ Valid image URL found:', imageUrl);
        console.log('Froala response format should be:', { link: imageUrl });
      } else {
        console.error('❌ No valid image URL found in response');
        console.log('Available response keys:', Object.keys(parsedResponse || {}));
      }
      
    } catch (parseError) {
      console.error('❌ Error parsing response:', parseError);
    }
    
    console.log('=== END DEBUG ===');
  }

  /**
   * Test image upload with debug logging
   * Call this from console to trigger an image upload with enhanced debugging
   */
  public testImageUploadDebug(): void {
    console.log('=== TEST IMAGE UPLOAD DEBUG ===');
    
    if (!this.editor || !this.editorInitialized) {
      console.error('Editor not ready for testing');
      return;
    }
    
    console.log('Editor image upload configuration:', {
      imageUpload: this.editor.opts?.imageUpload,
      imageUploadURL: this.editor.opts?.imageUploadURL,
      imageUploadMethod: this.editor.opts?.imageUploadMethod,
      imageUploadParam: this.editor.opts?.imageUploadParam,
      imageUploadParams: this.editor.opts?.imageUploadParams,
      requestHeaders: this.editor.opts?.requestHeaders,
      imagePasteProcess: this.editor.opts?.imagePasteProcess
    });
    
    console.log('To test upload, paste an image or use file picker in the editor');
    console.log('Watch the console for detailed upload flow logs');
    console.log('=== END TEST SETUP ===');
  }

  /**
   * Component lifecycle - OnDestroy
   */
  ngOnDestroy(): void {
    console.log('FroalaEditorComponent ngOnDestroy called');
    this.isFullscreenMode = false;

    // Stop guarding navigation once this editor instance is gone
    this.unsavedChangesService.clearGuard(this.unsavedChangesGuardHandler);

    // Cleanup ruler resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.a4ScaleObserver) {
      this.a4ScaleObserver.disconnect();
      this.a4ScaleObserver = null;
    }

    if (this.a4ScaleFrameId !== null) {
      cancelAnimationFrame(this.a4ScaleFrameId);
      this.a4ScaleFrameId = null;
    }
    
    // Cleanup resources first
    this.cleanup();
    
    // Destroy editor safely
    if (this.editor && typeof this.editor._editor.destroy === 'function') {
      try {
        this.editor._editor.destroy();
      } catch (error) {
        console.warn('Error destroying editor:', error);
      }
    }
    
    // Clear any remaining global references
    if ((window as any).froalaDebug === this) {
      delete (window as any).froalaDebug;
    }
    if ((window as any).froalaComponent === this) {
      delete (window as any).froalaComponent;
    }
    
    console.log('FroalaEditorComponent destroyed successfully');
  }
}
